import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Modal,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import greyDialogTitleSx from '../../components/Dialog/greyDialogTitleSx';
import cancelButtonSx from '../../components/Dialog/cancelButtonSx';
import ConfirmDialog from '../../components/Dialog/ConfirmDialog';
import '../../components/Invoice/Invoice.css'; // provides the "c-label" field label style
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import NavigateBeforeOutlinedIcon from '@mui/icons-material/NavigateBeforeOutlined';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { SearchIcon } from 'lucide-react';
import { GridColDef, GridValueGetterParams } from '@mui/x-data-grid';
import axios from '../../utils/axios';
import { toast } from 'react-toastify';
import CustomDataGrid from '../../components/DataGrid/DataGrid';
import { defaultRowsPerPageOptions } from '../../utils/api.types';
import { useAppSelector } from '../../store/hooks';
import { decryptSalaryConfig, decryptSalaryGrade } from '../../utils/salaryCrypto';

type PayrollTab = 'salaryHead' | 'salaryGrade' | 'employeeSalaryConfig' | 'loan' | 'headType' | 'salarySetup';

const tabConfig: { key: PayrollTab; label: string }[] = [
  { key: 'headType', label: 'Head Types' },
  { key: 'salaryHead', label: 'Salary Heads' },
  { key: 'salarySetup', label: 'Salary Setup' },
  { key: 'salaryGrade', label: 'Salary Grades' },
  { key: 'employeeSalaryConfig', label: 'Employee Salary Config' },
  { key: 'loan', label: 'Loans' }
];

const defaultSalaryHeadForm = {
  _id: '',
  headName: '',
  alias: '',
  type: 'Custom',
  componentType: 'Earning',
  calculationType: 'Fixed Amount',
  defaultAmount: '',
  percentage: '',
  headType: '',
  newHeadType: '',
  priority: '',
  isFixed: false,
  isTaxable: true,
  includeInCTC: true,
  includeForPF: false,
  includeForESI: false,
  showInPayslip: true,
  calculateOnProRata: true,
  isFlexibleBenefit: false,
  pfApplicabilityRule: 'Always' as 'Always' | 'WageBelow15k',
  isActive: true
};

const defaultSalaryGradeForm = {
  _id: '',
  gradeName: '',
  gradeValue: ''
};

// Friendly label for a head's calculation type (matches the Add dialog options).
const calculationTypeLabel = (ct?: string) => {
  switch (ct) {
    case 'Fixed Amount': return 'Flat Amount';
    case 'Percentage of Basic': return '% of Basic';
    case 'Percentage of CTC': return '% of CTC';
    case 'Percentage of Gross': return '% of Gross';
    case 'Balance': return 'Balance (Remaining)';
    case 'Manual': return 'Manual';
    default: return ct || '-';
  }
};

const defaultEmployeeSalaryConfigForm = {
  _id: '',
  employee: '',
  salaryGrade: '',
  effectiveFrom: '',
  salaryType: 'Monthly',
  ctcAmount: '',
  earnings: [] as Array<{ head: string; value: string; percent: string }>,
  deductions: [] as Array<{ head: string; value: string; percent: string }>
};

const defaultLoanForm = {
  _id: '',
  employee: '',
  loanType: 'Salary Advance',
  purpose: '',
  principalAmount: '',
  emiAmount: '',
  interestRate: '',
  totalMonths: '',
  paidMonths: '',
  startDate: '',
  endDate: '',
  status: 'Active',
  remarks: ''
};

const endpointByTab: Record<PayrollTab, string> = {
  salaryHead: '/api/v1/payroll/salaryHead',
  salaryGrade: '/api/v1/payroll/salaryGrade',
  employeeSalaryConfig: '/api/v1/payroll/employeeSalaryConfig',
  loan: '/api/v1/payroll/loan',
  headType: '/api/v1/payroll/salaryHeadType',
  salarySetup: '/api/v1/payroll/companySalaryHead'
};

const PayrollSettings: FC = () => {
  const { selectedAccount } = useAppSelector(state => state.auth);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState<PayrollTab>(() => {
    const t = searchParams.get('tab') as PayrollTab;
    return t && tabConfig.some(c => c.key === t) ? t : 'headType';
  });
  const [rows, setRows] = useState<any[]>([]);
  const [deleteTarget, setDeleteTarget] = useState<any>(null);
  const [deleting, setDeleting] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [salaryHeadForm, setSalaryHeadForm] = useState(defaultSalaryHeadForm);
  // System default heads: only the amount/percentage is editable.
  const [isDefaultHeadEdit, setIsDefaultHeadEdit] = useState(false);
  const [salaryGradeForm, setSalaryGradeForm] = useState(defaultSalaryGradeForm);
  const [employeeSalaryConfigForm, setEmployeeSalaryConfigForm] = useState(defaultEmployeeSalaryConfigForm);
  const [loanForm, setLoanForm] = useState(defaultLoanForm);
  const [headTypes, setHeadTypes] = useState<any[]>([]);
  const [headTypeDialogOpen, setHeadTypeDialogOpen] = useState(false);
  const [newHeadTypeName, setNewHeadTypeName] = useState('');
  const [editingHeadTypeId, setEditingHeadTypeId] = useState<string>('');
  const [unlockedPercentRows, setUnlockedPercentRows] = useState<Set<string>>(new Set());

  const togglePercentLock = (key: string) => {
    setUnlockedPercentRows(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };
  const [savingHeadType, setSavingHeadType] = useState(false);
  const [salaryHeads, setSalaryHeads] = useState<any[]>([]);
  const [salaryGrades, setSalaryGrades] = useState<any[]>([]);
  const [tdsRateOptions, setTdsRateOptions] = useState<any[]>([]);
  const [selectedTdsId, setSelectedTdsId] = useState<string>('');
  const [employees, setEmployees] = useState<any[]>([]);
  const [loanEmployee, setLoanEmployee] = useState<any>(null);
  const empSearchTimer = useRef<any>(null);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const effectiveFromInputRef = useRef<HTMLInputElement | null>(null);

  const [salarySetupDocId, setSalarySetupDocId] = useState<string>('');
  const [salarySetupAllowanceIds, setSalarySetupAllowanceIds] = useState<string[]>([]);
  const [salarySetupDeductionIds, setSalarySetupDeductionIds] = useState<string[]>([]);
  const [salarySetupLoading, setSalarySetupLoading] = useState(true);
  const [salarySetupSaving, setSalarySetupSaving] = useState(false);
  const [addHeadDialog, setAddHeadDialog] = useState<{ open: boolean; section: 'allowance' | 'deduction' }>({ open: false, section: 'allowance' });
  const [addHeadSelected, setAddHeadSelected] = useState<any>(null);


  // Salary Head sub-tab (4 categories)
  type SalaryHeadSubTab = string;
  const [salaryHeadSubTab, setSalaryHeadSubTab] = useState<SalaryHeadSubTab>('');
  const salaryHeadSubTabConfig = useMemo(
    () => headTypes
      .slice()
      .sort((a: any, b: any) => String(a.name || '').localeCompare(String(b.name || '')))
      .map((t: any) => ({ key: t._id as string, label: t.name as string })),
    [headTypes]
  );

  useEffect(() => {
    if (!salaryHeadSubTab && salaryHeadSubTabConfig.length > 0) {
      setSalaryHeadSubTab(salaryHeadSubTabConfig[0].key);
    }
    if (salaryHeadSubTab && salaryHeadSubTabConfig.length > 0 && !salaryHeadSubTabConfig.find(s => s.key === salaryHeadSubTab)) {
      setSalaryHeadSubTab(salaryHeadSubTabConfig[0].key);
    }
  }, [salaryHeadSubTabConfig, salaryHeadSubTab]);

  const getDecimalValue = (value: any) => {
    if (value === null || value === undefined || value === '') return 0;
    if (typeof value === 'object' && value.$numberDecimal !== undefined) return Number(value.$numberDecimal || 0);
    return Number(value || 0);
  };

  const formatCurrency = (value: any) => `₹${getDecimalValue(value).toLocaleString('en-IN')}`;

  const activeTabIndex = useMemo(() => tabConfig.findIndex(item => item.key === tab), [tab]);
  const activeTab = tabConfig[activeTabIndex];
  const configuredSalaryEmployeeIds = useMemo(() => {
    if (tab !== 'employeeSalaryConfig') return new Set<string>();
    return new Set(
      rows
        .map((row: any) => row.employee?._id || row.employee)
        .filter(Boolean)
        .map(String)
    );
  }, [rows, tab]);
  const salaryConfigEmployeeOptions = useMemo(() => {
    if (employeeSalaryConfigForm._id) return employees;
    return employees.filter((employee: any) => !configuredSalaryEmployeeIds.has(String(employee._id)));
  }, [configuredSalaryEmployeeIds, employeeSalaryConfigForm._id, employees]);
  const filteredRows = useMemo(() => {
    const query = searchInput.trim().toLowerCase();
    let base = rows;
    if (tab === 'salaryHead') {
      const filteredBySubTab = query
        ? rows
        : rows.filter(r => {
            const rowHeadTypeId = r.headType?._id || r.headType;
            return rowHeadTypeId === salaryHeadSubTab;
          });
      base = filteredBySubTab
        .slice()
        .sort((a, b) => String(a.headName || '').localeCompare(String(b.headName || '')));
    }
    if (tab === 'headType') {
      base = rows.slice().sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
    }
    if (tab === 'salaryGrade') {
      base = rows.slice().sort((a, b) => String(a.gradeName || '').localeCompare(String(b.gradeName || '')));
    }
    if (!query) return base;
    return base.filter(row => {
      const fields = [
        row.headName, row.alias, row.gradeName, row.name,
        row.componentType, row.calculationType, row.type,
        row.employee?.name, row.employee?.empId,
        row.headType?.name, row.salaryGrade?.gradeName,
        row.loanType, row.status, row.purpose, row.remarks
      ];
      return fields.some(f => String(f || '').toLowerCase().includes(query));
    });
  }, [rows, searchInput, tab, salaryHeadSubTab]);

  const loadRows = useCallback(async () => {
    if (tab === 'salarySetup') return;
    setRows([]);
    setLoading(true);
    try {
      const response = await axios.get(`${endpointByTab[tab]}?limit=100`);
      let data = response.data?.data || [];
      // Amounts arrive ENCRYPTED from the API — decrypt client-side for display.
      if (tab === 'employeeSalaryConfig') {
        data = data.map((cfg: any) => decryptSalaryConfig(cfg));
      } else if (tab === 'salaryGrade') {
        data = data.map((g: any) => decryptSalaryGrade(g));
      }
      setRows(data);
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to load payroll data');
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const loadSalarySetupRef = useRef<() => void>(() => {});

  useEffect(() => {
    if (tab === 'salarySetup') {
      loadSalarySetupRef.current();
    }
  }, [tab]);

  const loadHeadTypes = useCallback(async () => {
    try {
      const response = await axios.get('/api/v1/payroll/salaryHeadType?limit=100');
      setHeadTypes(response.data?.data || []);
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to load salary head types');
    }
  }, []);

  const loadTdsRates = useCallback(async () => {
    try {
      const res = await axios.get('/api/v1/gst/tax-rates');
      const all: any[] = res.data?.taxRates || res.data?.data || [];
      const tds = all.filter((r: any) => r.taxType === 'tds').map((r: any) => ({
        value: r._id,
        label: `${r.taxRate}% - ${r.taxSection || ''} ${r.taxName || ''}`.trim(),
        rate: r.taxRate,
        taxSection: r.taxSection,
        taxName: r.taxName
      }));
      setTdsRateOptions(tds);
    } catch (err) {
      // silent
    }
  }, []);

  useEffect(() => {
    loadHeadTypes();
    loadTdsRates();
  }, [loadHeadTypes, loadTdsRates]);

  // When tdsRateOptions loads, apply percent to TDS deduction row if a rate is selected
  useEffect(() => {
    if (!selectedTdsId || !tdsRateOptions.length) return;
    const opt = tdsRateOptions.find((o: any) => o.value === selectedTdsId);
    if (!opt) return;
    setEmployeeSalaryConfigForm((prev: any) => ({
      ...prev,
      deductions: prev.deductions.map((d: any) => /^tds$/i.test(d.head)
        ? { ...d, percent: String(opt.rate) }
        : d)
    }));
  }, [tdsRateOptions, selectedTdsId]);

  // Server-side search, 10 at a time (filter matches name / empId / email / phone).
  const loadEmployees = useCallback(async (search = '') => {
    if (!selectedAccount?._id) return;
    try {
      const params = new URLSearchParams({ limit: '10' });
      if (search) params.append('filter', search);
      const response = await axios.get(`/api/v1/employee/account/${selectedAccount._id}/emp-list?${params}`);
      setEmployees(response.data?.employees || []);
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to load employees');
    }
  }, [selectedAccount?._id]);

  const loadSalaryHeads = useCallback(async () => {
    try {
      const response = await axios.get('/api/v1/payroll/salaryHead?limit=200');
      setSalaryHeads(response.data?.data || []);
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to load salary heads');
    }
  }, []);

  const loadSalaryGrades = useCallback(async () => {
    try {
      const response = await axios.get('/api/v1/payroll/salaryGrade?limit=100');
      setSalaryGrades(response.data?.data || []);
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to load salary grades');
    }
  }, []);

  const loadSalarySetup = useCallback(async () => {
    setSalarySetupLoading(true);
    try {
      let heads = salaryHeads;
      if (!heads.length) {
        const hr = await axios.get('/api/v1/payroll/salaryHead?limit=500');
        heads = hr.data?.data || [];
        setSalaryHeads(heads);
      }
      const res = await axios.get('/api/v1/payroll/companySalaryHead?limit=10');
      const doc = (res.data?.data || [])[0];
      if (doc) {
        setSalarySetupDocId(doc._id);
        const allowIds = [
          ...(doc.companyDefaultAllowanceHeads || []),
          ...(doc.companyCustomAllowanceHeads || [])
        ].map((x: any) => String(x?._id || x));
        const dedIds = [
          ...(doc.employeePFContributionHeads || []),
          ...(doc.employeeESIContributionHeads || []),
          ...(doc.customeDeductionsHeads || [])
        ].map((x: any) => String(x?._id || x));
        setSalarySetupAllowanceIds(allowIds);
        setSalarySetupDeductionIds(dedIds);
      } else {
        // No setup yet — show the empty state; defaults are added only via "Default Setup".
        setSalarySetupDocId('');
        setSalarySetupAllowanceIds([]);
        setSalarySetupDeductionIds([]);
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to load salary setup');
    } finally {
      setSalarySetupLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salaryHeads]);

  // "Default Setup" button — ask the backend to seed/top-up this account's
  // Salary Setup with the standard default heads, then refresh the view.
  const applyDefaultSalarySetup = useCallback(async () => {
    setSalarySetupSaving(true);
    try {
      await axios.post('/api/v1/payroll/companySalaryHead/seed-defaults', {});
      toast.success('Default salary setup applied');
      await loadSalarySetup();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to apply default setup');
    } finally {
      setSalarySetupSaving(false);
    }
  }, [loadSalarySetup]);

  useEffect(() => {
    loadSalarySetupRef.current = loadSalarySetup;
  }, [loadSalarySetup]);

  const persistSalarySetup = async (allowanceIds: string[], deductionIds: string[]) => {
    setSalarySetupSaving(true);
    try {
      const isEarning = (h: any) => ['Earning', 'Reimbursement'].includes(h?.componentType || 'Earning');
      const isDeduction = (h: any) => ['Deduction', 'Employer Contribution'].includes(h?.componentType || '');
      const allowDefault: string[] = [];
      const allowCustom: string[] = [];
      allowanceIds.forEach(id => {
        const h = salaryHeads.find((x: any) => String(x._id) === id);
        if (!h || !isEarning(h)) return;
        if (h.type === 'Default' || h.isDefault) allowDefault.push(id);
        else allowCustom.push(id);
      });
      const dedCustom: string[] = [];
      deductionIds.forEach(id => {
        const h = salaryHeads.find((x: any) => String(x._id) === id);
        if (!h || !isDeduction(h)) return;
        dedCustom.push(id);
      });
      const payload = {
        companyDefaultAllowanceHeads: allowDefault,
        companyCustomAllowanceHeads: allowCustom,
        customeDeductionsHeads: dedCustom,
        // All deductions live in customeDeductionsHeads; clear these so a removed PF/ESI doesn't reappear on reload.
        employeePFContributionHeads: [],
        employeeESIContributionHeads: [],
        isActive: true,
        isDelete: false
      };
      if (salarySetupDocId) {
        await axios.put(`/api/v1/payroll/companySalaryHead/${salarySetupDocId}`, payload);
      } else {
        const res = await axios.post('/api/v1/payroll/companySalaryHead', payload);
        const newId = res.data?.data?._id;
        if (newId) setSalarySetupDocId(newId);
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to save salary setup');
    } finally {
      setSalarySetupSaving(false);
    }
  };

  const addSetupHead = (section: 'allowance' | 'deduction', id: string) => {
    if (section === 'allowance') {
      const next = salarySetupAllowanceIds.includes(id) ? salarySetupAllowanceIds : [...salarySetupAllowanceIds, id];
      setSalarySetupAllowanceIds(next);
      persistSalarySetup(next, salarySetupDeductionIds);
    } else {
      const next = salarySetupDeductionIds.includes(id) ? salarySetupDeductionIds : [...salarySetupDeductionIds, id];
      setSalarySetupDeductionIds(next);
      persistSalarySetup(salarySetupAllowanceIds, next);
    }
  };

  const removeSetupHead = (section: 'allowance' | 'deduction', id: string) => {
    if (section === 'allowance') {
      const next = salarySetupAllowanceIds.filter(x => x !== id);
      setSalarySetupAllowanceIds(next);
      persistSalarySetup(next, salarySetupDeductionIds);
    } else {
      const next = salarySetupDeductionIds.filter(x => x !== id);
      setSalarySetupDeductionIds(next);
      persistSalarySetup(salarySetupAllowanceIds, next);
    }
  };

  const openCreateForm = async () => {
    setIsDefaultHeadEdit(false);
    if (tab === 'headType') {
      setNewHeadTypeName('');
      setHeadTypeDialogOpen(true);
      return;
    }
    if (tab === 'salaryHead') {
      await loadHeadTypes();
      const subTypeName = headTypes.find((t: any) => t._id === salaryHeadSubTab)?.name || '';
      const inferredComponent =
        subTypeName === 'Deduction' ? 'Deduction'
        : subTypeName === 'Reimbursement' ? 'Reimbursement'
        : subTypeName === 'Employer Contribution' ? 'Employer Contribution'
        : 'Earning';
      setSalaryHeadForm({ ...defaultSalaryHeadForm, headType: salaryHeadSubTab, componentType: inferredComponent } as any);
    }
    if (tab === 'salaryGrade') {
      setSalaryGradeForm(defaultSalaryGradeForm);
    }
    if (tab === 'employeeSalaryConfig') {
      navigate('/people/payroll-settings/salary-config/new');
      return;
    }
    if (tab === 'loan') {
      setLoanForm(defaultLoanForm);
      setLoanEmployee(null);
      loadEmployees();
    }
    setDrawerOpen(true);
  };

  const openEditForm = async (row: any) => {
    // NOTE: guard against a stale `tab` value: decide the edit target from the ROW's own
    // shape, not just the tab. A salary-head row has `headName` + `calculationType`;
    // an employee-salary-config row has `companyAllowanceHeads` / `ctcAmount`. This
    // stops a Salary Head edit from wrongly opening the Salary Config form.
    const looksLikeSalaryHead = row && (row.calculationType !== undefined || (row.headName !== undefined && row.companyAllowanceHeads === undefined));
    const looksLikeSalaryConfig = row && (row.companyAllowanceHeads !== undefined || row.ctcAmount !== undefined);

    if (tab === 'headType' && !looksLikeSalaryHead && !looksLikeSalaryConfig) {
      setEditingHeadTypeId(row._id || '');
      setNewHeadTypeName(row.name || '');
      setHeadTypeDialogOpen(true);
      return;
    }
    if (tab === 'salaryHead' || (looksLikeSalaryHead && !looksLikeSalaryConfig)) {
      await loadHeadTypes();
      setIsDefaultHeadEdit(row.type === 'Default' || !!row.isDefault);
      setSalaryHeadForm({
        ...defaultSalaryHeadForm,
        _id: row._id || '',
        headName: row.headName || '',
        alias: row.alias || '',
        type: row.type || 'Custom',
        componentType: row.componentType || 'Earning',
        calculationType: row.calculationType || 'Fixed Amount',
        defaultAmount: row.defaultAmount || '',
        percentage: row.percentage || '',
        headType: row.headType?._id || row.headType || '',
        priority: row.priority || '',
        isFixed: !!row.isFixed,
        isTaxable: row.isTaxable !== false,
        includeInCTC: row.includeInCTC !== false,
        includeForPF: !!row.includeForPF,
        includeForESI: !!row.includeForESI,
        showInPayslip: row.showInPayslip !== false,
        calculateOnProRata: row.calculateOnProRata !== false,
        isFlexibleBenefit: !!row.isFlexibleBenefit,
        pfApplicabilityRule: row.pfApplicabilityRule || 'Always',
        isActive: row.isActive !== false,
        newHeadType: ''
      });
      setDrawerOpen(false);
      setTimeout(() => setDrawerOpen(true), 50);
      return;
    }
    if (tab === 'salaryGrade') {
      setSalaryGradeForm({
        _id: row._id || '',
        gradeName: row.gradeName || '',
        gradeValue: row.gradeValue || ''
      });
    }
    if (tab === 'employeeSalaryConfig' && looksLikeSalaryConfig) {
      navigate(`/people/payroll-settings/salary-config/${row._id}/edit`);
      return;
    }
    if (tab === 'loan') {
      loadEmployees();
      setLoanEmployee(row.employee?._id ? row.employee : null);
      setLoanForm({
        ...defaultLoanForm,
        _id: row._id || '',
        employee: row.employee?._id || row.employee || '',
        loanType: row.loanType || 'Salary Advance',
        purpose: row.purpose || '',
        principalAmount: String(row.principalAmount || ''),
        emiAmount: String(row.emiAmount || ''),
        interestRate: String(row.interestRate || ''),
        totalMonths: String(row.totalMonths || ''),
        paidMonths: String(row.paidMonths || 0),
        startDate: row.startDate ? String(row.startDate).slice(0, 10) : '',
        endDate: row.endDate ? String(row.endDate).slice(0, 10) : '',
        status: row.status || 'Active',
        remarks: row.remarks || ''
      });
    }
    setDrawerOpen(false);
    setTimeout(() => setDrawerOpen(true), 50);
  };

  const handleSaveLoan = async () => {
    if (!loanForm.employee || !loanForm.principalAmount || !loanForm.emiAmount || !loanForm.totalMonths || !loanForm.startDate) {
      toast.error('Employee, Principal, EMI, Total Months, Start Date required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        employee: loanForm.employee,
        loanType: loanForm.loanType,
        purpose: loanForm.purpose,
        principalAmount: Number(loanForm.principalAmount),
        emiAmount: Number(loanForm.emiAmount),
        interestRate: loanForm.interestRate ? Number(loanForm.interestRate) : 0,
        totalMonths: Number(loanForm.totalMonths),
        paidMonths: loanForm.paidMonths ? Number(loanForm.paidMonths) : 0,
        startDate: loanForm.startDate,
        endDate: loanForm.endDate || undefined,
        status: loanForm.status,
        remarks: loanForm.remarks
      };
      if (loanForm._id) {
        await axios.put(`/api/v1/payroll/loan/${loanForm._id}`, payload);
        toast.success('Loan updated');
      } else {
        await axios.post('/api/v1/payroll/loan', payload);
        toast.success('Loan created');
      }
      setDrawerOpen(false);
      await loadRows();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to save loan');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSalaryHead = async () => {
    if (!salaryHeadForm.headName.trim()) {
      toast.error('Salary head name is required');
      return;
    }

    // Only one salary head may use the "Balance (Remaining)" calculation type.
    if (salaryHeadForm.calculationType === 'Balance') {
      const existingBalance = salaryHeads.find(
        (h: any) => h.calculationType === 'Balance' && String(h._id) !== String(salaryHeadForm._id)
      );
      if (existingBalance) {
        toast.error(`"${existingBalance.headName}" already uses Balance (Remaining). Only one salary head can use it.`);
        return;
      }
    }

    setSaving(true);
    try {
      let headType = salaryHeadForm.headType;
      if (!headType && salaryHeadForm.newHeadType.trim()) {
        const response = await axios.post('/api/v1/payroll/salaryHeadType', { name: salaryHeadForm.newHeadType.trim() });
        headType = response.data?.data?._id;
      }
      if (!headType) {
        toast.error('Salary head type is required');
        setSaving(false);
        return;
      }

      const payload = {
        headName: salaryHeadForm.headName,
        alias: salaryHeadForm.alias,
        type: salaryHeadForm.type,
        componentType: salaryHeadForm.componentType,
        calculationType: salaryHeadForm.calculationType,
        defaultAmount: salaryHeadForm.defaultAmount ? Number(salaryHeadForm.defaultAmount) : 0,
        percentage: salaryHeadForm.percentage ? Number(salaryHeadForm.percentage) : 0,
        headType,
        priority: salaryHeadForm.priority ? Number(salaryHeadForm.priority) : undefined,
        isFixed: salaryHeadForm.isFixed,
        isTaxable: salaryHeadForm.isTaxable,
        includeInCTC: salaryHeadForm.includeInCTC,
        includeForPF: salaryHeadForm.includeForPF,
        includeForESI: salaryHeadForm.includeForESI,
        showInPayslip: salaryHeadForm.showInPayslip,
        calculateOnProRata: salaryHeadForm.calculateOnProRata,
        isFlexibleBenefit: salaryHeadForm.isFlexibleBenefit,
        pfApplicabilityRule: salaryHeadForm.pfApplicabilityRule,
        isActive: salaryHeadForm.isActive
      };
      if (salaryHeadForm._id) {
        await axios.put(`/api/v1/payroll/salaryHead/${salaryHeadForm._id}`, payload);
        toast.success('Salary head updated');
      } else {
        await axios.post('/api/v1/payroll/salaryHead', payload);
        toast.success('Salary head created');
      }
      setDrawerOpen(false);
      await loadRows();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to save salary head');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSalaryGrade = async () => {
    if (!salaryGradeForm.gradeName.trim()) {
      toast.error('Grade name is required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        gradeName: salaryGradeForm.gradeName,
        gradeValue: Number(salaryGradeForm.gradeValue || 0)
      };
      if (salaryGradeForm._id) {
        await axios.put(`/api/v1/payroll/salaryGrade/${salaryGradeForm._id}`, payload);
        toast.success('Salary grade updated');
      } else {
        await axios.post('/api/v1/payroll/salaryGrade', payload);
        toast.success('Salary grade created');
      }
      setDrawerOpen(false);
      await loadRows();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to save salary grade');
    } finally {
      setSaving(false);
    }
  };

  const buildAmountItems = (items: Array<{ head: string; value: string; percent?: string }>) => {
    return items
      .filter(item => item.head && item.value !== '' && Number(item.value) > 0)
      .map(item => ({
        head: item.head,
        value: Number(item.value),
        percent: item.percent ? Number(item.percent) : 0
      }));
  };

  const updateSalaryConfigRow = (section: 'earnings' | 'deductions', index: number, field: 'head' | 'value' | 'percent', value: string) => {
    setEmployeeSalaryConfigForm(prev => {
      const ctc = Number(prev.ctcAmount || 0);
      const monthlyCtc = ctc / 12;
      return {
        ...prev,
        [section]: prev[section].map((item, idx) => {
          if (idx !== index) return item;
          const updated: any = { ...item, [field]: value };
          if (field === 'percent' && monthlyCtc) {
            const pct = Number(value || 0);
            updated.value = pct > 0 ? String(Number((monthlyCtc * pct / 100).toFixed(2))) : '';
          } else if (field === 'value' && monthlyCtc) {
            const v = Number(value || 0);
            updated.percent = v > 0 ? String(Number((v / monthlyCtc * 100).toFixed(2))) : '';
          }
          return updated;
        })
      };
    });
  };

  const getHeadOptions = (section: 'earnings' | 'deductions') => {
    const wanted = section === 'earnings'
      ? ['Earning', 'Reimbursement']
      : ['Deduction', 'Employer Contribution'];
    return salaryHeads.filter(head => wanted.includes(head.componentType || 'Earning'));
  };

  const calcAmountForHead = (head: any, annualCtc: number, basicMonthly: number) => {
    if (!head) return 0;
    const monthly = annualCtc / 12;
    if (head.calculationType === 'Fixed Amount') return Number(head.defaultAmount || 0);
    if (head.calculationType === 'Percentage of Basic') return Number(((head.percentage || 0) / 100 * basicMonthly).toFixed(2));
    if (head.calculationType === 'Percentage of CTC' || head.calculationType === 'Percentage of Gross')
      return Number(((head.percentage || 0) / 100 * monthly).toFixed(2));
    return 0;
  };

  const calcEarningsFromCTC = (annualCtc: number, heads: any[]) => {
    const monthly = annualCtc / 12;
    const basic = Number((monthly * 0.5).toFixed(2));
    return heads.map(h => {
      const val = calcAmountForHead(h, annualCtc, basic);
      return { head: h.headName, value: val > 0 ? String(val) : '' };
    });
  };

  const recalcRowsFromCTC = (rows: Array<{ head: string; value: string; percent: string }>, annualCtc: number) => {
    const monthly = annualCtc / 12;
    const basicHead = salaryHeads.find(h => /basic/i.test(h.headName));
    const basicAmount = basicHead
      ? calcAmountForHead(basicHead, annualCtc, monthly * 0.5)
      : Number((monthly * 0.5).toFixed(2));
    return rows.map(r => {
      const head = salaryHeads.find(h => h.headName === r.head);
      if (!head) return r;
      const val = calcAmountForHead(head, annualCtc, basicAmount);
      return { head: r.head, value: val > 0 ? String(val) : r.value, percent: r.percent };
    });
  };

  const splitHeads = (heads: any[]) => {
    const deductionTypes = ['Deduction', 'Employer Contribution'];
    return {
      earningHeads: heads.filter(h => !deductionTypes.includes(h.componentType || '')),
      deductionHeads: heads.filter(h => deductionTypes.includes(h.componentType || ''))
    };
  };

  const handleGradeChange = (gradeId: string) => {
    const grade = salaryGrades.find(item => item._id === gradeId);
    const annualCtc = getDecimalValue(grade?.gradeValue);
    setEmployeeSalaryConfigForm(prev => {
      const effectiveCtc = annualCtc || Number(prev.ctcAmount) || 0;
      return {
        ...prev,
        salaryGrade: gradeId,
        ctcAmount: annualCtc ? String(annualCtc) : prev.ctcAmount,
        earnings: effectiveCtc ? recalcRowsFromCTC(prev.earnings, effectiveCtc) : prev.earnings,
        deductions: effectiveCtc ? recalcRowsFromCTC(prev.deductions, effectiveCtc) : prev.deductions
      };
    });
  };

  const handleCtcAmountChange = (value: string) => {
    const annualCtc = Number(value || 0);
    setEmployeeSalaryConfigForm(prev => ({
      ...prev,
      ctcAmount: value,
      earnings: annualCtc ? recalcRowsFromCTC(prev.earnings, annualCtc) : prev.earnings,
      deductions: annualCtc ? recalcRowsFromCTC(prev.deductions, annualCtc) : prev.deductions
    }));
  };

  const handleHeadSelect = (section: 'earnings' | 'deductions', index: number, headName: string) => {
    const annualCtc = Number(employeeSalaryConfigForm.ctcAmount || 0);
    const monthly = annualCtc / 12;
    const basicHead = salaryHeads.find(h => /basic/i.test(h.headName));
    const basicAmount = basicHead ? calcAmountForHead(basicHead, annualCtc, monthly * 0.5) : monthly * 0.5;
    const head = salaryHeads.find(h => h.headName === headName);
    let val = 0;
    let prefillPercent = '';
    if (head) {
      if (annualCtc) {
        val = calcAmountForHead(head, annualCtc, basicAmount);
      } else if (head.calculationType === 'Fixed Amount' && Number(head.defaultAmount) > 0) {
        val = Number(head.defaultAmount);
      }
      if (head.percentage && Number(head.percentage) > 0) {
        prefillPercent = String(head.percentage);
      } else if (head.calculationType === 'Fixed Amount' && Number(head.defaultAmount) > 0 && !val) {
        val = Number(head.defaultAmount);
      }
    }
    setEmployeeSalaryConfigForm(prev => ({
      ...prev,
      [section]: prev[section].map((item, idx) => idx === index ? { head: headName, value: val > 0 ? String(val) : '', percent: prefillPercent } : item)
    }));
  };

  const handleAnnualGradeValueChange = (value: string) => {
    setSalaryGradeForm(prev => ({
      ...prev,
      gradeValue: value
    }));
  };

  const addSalaryConfigRow = (section: 'earnings' | 'deductions') => {
    setEmployeeSalaryConfigForm(prev => ({
      ...prev,
      [section]: [...prev[section], { head: '', value: '', percent: '' }]
    }));
  };

  const removeSalaryConfigRow = (section: 'earnings' | 'deductions', index: number) => {
    setEmployeeSalaryConfigForm(prev => ({
      ...prev,
      [section]: prev[section].filter((_, idx) => idx !== index)
    }));
  };

  const totalAmount = (items: Array<{ value: string }>) => {
    return items.reduce((sum, item) => sum + (Number(item.value) || 0), 0);
  };


  const handleSaveEmployeeSalaryConfig = async () => {
    const earnings = buildAmountItems(employeeSalaryConfigForm.earnings);
    if (!employeeSalaryConfigForm.employee || !employeeSalaryConfigForm.effectiveFrom || !employeeSalaryConfigForm.ctcAmount) {
      toast.error('Employee, effective date and annual CTC are required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        employee: employeeSalaryConfigForm.employee,
        salaryGrade: employeeSalaryConfigForm.salaryGrade || undefined,
        effectiveFrom: employeeSalaryConfigForm.effectiveFrom,
        salaryType: employeeSalaryConfigForm.salaryType,
        salryType: employeeSalaryConfigForm.salaryType,
        ctcAmount: Number(employeeSalaryConfigForm.ctcAmount || 0),
        companyAllowanceHeads: earnings,
        deductionsHeads: buildAmountItems(employeeSalaryConfigForm.deductions),
        tdsRateId: selectedTdsId || undefined
      };
      if (employeeSalaryConfigForm._id) {
        await axios.put(`/api/v1/payroll/employeeSalaryConfig/${employeeSalaryConfigForm._id}`, payload);
        toast.success('Employee salary config updated');
      } else {
        await axios.post('/api/v1/payroll/employeeSalaryConfig', payload);
        toast.success('Employee salary config created');
      }
      setDrawerOpen(false);
      await loadRows();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to save employee salary config');
    } finally {
      setSaving(false);
    }
  };

  // Per-tab wording for the delete confirm: record type + the row's display name.
  const deleteLabel = (() => {
    const r = deleteTarget || {};
    if (tab === 'salaryHead') return { type: salaryHeadSubTabConfig.find(s => s.key === salaryHeadSubTab)?.label.replace(/s$/, '') || 'Salary Head', name: r.headName };
    if (tab === 'salaryGrade') return { type: 'Salary Grade', name: r.gradeName };
    if (tab === 'loan') return { type: 'Loan', name: r.employee?.name ? `${r.employee.name}'s loan` : '' };
    if (tab === 'employeeSalaryConfig') return { type: 'Salary Config', name: r.employee?.name || r.name };
    return { type: 'Head Type', name: r.name };
  })();

  const handleDelete = async () => {
    if (!deleteTarget?._id) return;
    setDeleting(true);
    try {
      await axios.delete(`${endpointByTab[tab]}/${deleteTarget._id}`);
      toast.success(`${deleteLabel.type} deleted`);
      setDeleteTarget(null);
      await loadRows();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || `Failed to delete ${deleteLabel.type.toLowerCase()}`);
    } finally {
      setDeleting(false);
    }
  };

  const actionButtonStyle = {
    height: 35,
    width: 35,
    border: '0.1px solid #49a563',
    color: '#49a563',
    backgroundColor: '#edf6ef'
  };

  const deleteButtonStyle = {
    height: 35,
    width: 35,
    border: '0.1px solid #d32f2f',
    color: '#d32f2f',
    backgroundColor: '#fff5f5'
  };

  const salaryHeadColumns: GridColDef[] = useMemo(() => {
    const yesNo = (v: boolean) => (
      <Chip
        size="small"
        label={v ? 'Yes' : 'No'}
        style={{ borderRadius: 7, color: '#000', backgroundColor: v ? '#e8f5e9' : '#eeeeee' }}
      />
    );
    const calcCell = (row: any) => {
      const ct = row.calculationType;
      if (ct === 'Fixed Amount') return `Fixed ₹${Number(row.defaultAmount || 0).toLocaleString('en-IN')}`;
      if (ct === 'Percentage of Basic') return `${row.percentage || 0}% of Basic`;
      if (ct === 'Percentage of CTC') return `${row.percentage || 0}% of CTC`;
      if (ct === 'Percentage of Gross') return `${row.percentage || 0}% of Gross`;
      if (ct === 'Balance') return 'Balance (Remaining)';
      if (ct === 'Manual') return 'Manual';
      return ct || 'Manual';
    };
    const nameCol: GridColDef = {
      field: 'headName',
      headerName: 'Name',
      flex: 1.4,
      renderCell: ({ row }: GridValueGetterParams) => (
        <Tooltip title={row.headName || '-'}>
          <strong>{row.headName}</strong>
        </Tooltip>
      )
    };
    const statusCol: GridColDef = {
      field: 'isActive',
      headerName: 'Status',
      flex: 0.7,
      renderCell: ({ row }: GridValueGetterParams) => (
        <Chip
          size="small"
          label={row.isActive !== false ? 'Active' : 'Inactive'}
          style={{ borderRadius: 7, color: '#000', backgroundColor: row.isActive !== false ? '#e8f5e9' : '#eeeeee' }}
        />
      )
    };
    const actionsCol: GridColDef = {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.8,
      sortable: false,
      renderCell: ({ row }: GridValueGetterParams) => (
        <Stack direction="row" spacing={1}>
          <Tooltip title="Edit"><IconButton size="small" onClick={() => openEditForm(row)} style={actionButtonStyle}><EditIcon style={{ fontSize: 16 }} /></IconButton></Tooltip>
          {!row.isDefault && (
            <Tooltip title="Delete"><IconButton size="small" onClick={() => setDeleteTarget(row)} style={deleteButtonStyle}><DeleteIcon style={{ fontSize: 16 }} /></IconButton></Tooltip>
          )}
        </Stack>
      )
    };

    const subTabName = headTypes.find((t: any) => t._id === salaryHeadSubTab)?.name || '';
    if (subTabName === 'Allowance' || subTabName === 'Variable') {
      return [
        nameCol,
        { field: 'calc', headerName: 'Calculation', flex: 1, renderCell: ({ row }: GridValueGetterParams) => calcCell(row) },
        { field: 'pf', headerName: 'EPF', flex: 0.5, renderCell: ({ row }: GridValueGetterParams) => yesNo(!!row.includeForPF) },
        { field: 'esi', headerName: 'ESI', flex: 0.5, renderCell: ({ row }: GridValueGetterParams) => yesNo(!!row.includeForESI) },
        statusCol,
        actionsCol
      ];
    }
    if (subTabName === 'Deduction') {
      return [
        nameCol,
        { field: 'calc', headerName: 'Calculation', flex: 1, renderCell: ({ row }: GridValueGetterParams) => calcCell(row) },
        statusCol,
        actionsCol
      ];
    }
    if (subTabName === 'Employer Contribution') {
      return [
        nameCol,
        { field: 'calc', headerName: 'Calculation', flex: 1, renderCell: ({ row }: GridValueGetterParams) => calcCell(row) },
        statusCol,
        actionsCol
      ];
    }
    if (subTabName === 'Reimbursement') {
      return [
        nameCol,
        { field: 'max', headerName: 'Max Amount', flex: 0.8, renderCell: ({ row }: GridValueGetterParams) => `₹${Number(row.defaultAmount || 0).toLocaleString('en-IN')}` },
        { field: 'taxable', headerName: 'Taxable', flex: 0.5, renderCell: ({ row }: GridValueGetterParams) => yesNo(!!row.isTaxable) },
        statusCol,
        actionsCol
      ];
    }
    // fallback for any custom head type
    return [
      nameCol,
      { field: 'calc', headerName: 'Calculation', flex: 1, renderCell: ({ row }: GridValueGetterParams) => calcCell(row) },
      statusCol,
      actionsCol
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salaryHeadSubTab, headTypes]);

  const loanColumns: GridColDef[] = useMemo(() => [
    {
      field: 'employee',
      headerName: 'Employee',
      flex: 1.4,
      renderCell: ({ row }: GridValueGetterParams) => (
        <Box>
          <strong>{row.employee?.name || '-'}</strong>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{row.employee?.empId || '—'}</Typography>
        </Box>
      )
    },
    { field: 'loanType', headerName: 'Type', flex: 0.9 },
    { field: 'principalAmount', headerName: 'Principal', flex: 0.8, align: 'right', headerAlign: 'right', renderCell: ({ row }: GridValueGetterParams) => `₹${Number(row.principalAmount || 0).toLocaleString('en-IN')}` },
    { field: 'emiAmount', headerName: 'EMI', flex: 0.7, align: 'right', headerAlign: 'right', renderCell: ({ row }: GridValueGetterParams) => `₹${Number(row.emiAmount || 0).toLocaleString('en-IN')}` },
    { field: 'progress', headerName: 'Progress', flex: 0.7, renderCell: ({ row }: GridValueGetterParams) => `${row.paidMonths || 0} / ${row.totalMonths || 0}` },
    { field: 'startDate', headerName: 'Start', flex: 0.8, renderCell: ({ row }: GridValueGetterParams) => row.startDate ? new Date(row.startDate).toLocaleDateString('en-IN') : '-' },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.7,
      renderCell: ({ row }: GridValueGetterParams) => (
        <Chip
          size="small"
          label={row.status || 'Active'}
          style={{
            borderRadius: 7,
            color: '#000',
            backgroundColor: row.status === 'Active' ? '#e8f5e9' : row.status === 'Closed' ? '#eeeeee' : row.status === 'Paused' ? '#fff7e6' : '#fee2e2'
          }}
        />
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.7,
      sortable: false,
      renderCell: ({ row }: GridValueGetterParams) => (
        <Stack direction="row" spacing={1}>
          <Tooltip title="Edit"><IconButton size="small" onClick={() => openEditForm(row)} style={actionButtonStyle}><EditIcon style={{ fontSize: 16 }} /></IconButton></Tooltip>
          <Tooltip title="Delete"><IconButton size="small" onClick={() => setDeleteTarget(row)} style={deleteButtonStyle}><DeleteIcon style={{ fontSize: 16 }} /></IconButton></Tooltip>
        </Stack>
      )
    }
  ], [tab]);

  const setupColumns: GridColDef[] = useMemo(() => [
    {
      field: 'primary',
      headerName: 'Name',
      flex: 1.7,
      valueGetter: ({ row }: GridValueGetterParams) => row.headName || row.gradeName || row.employee?.name || row.name || row._id || '',
      renderCell: ({ row }: GridValueGetterParams) => (
        <Tooltip title={row.headName || row.gradeName || row.employee?.name || row.name || row._id}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%', minWidth: 0 }}>
            <Typography noWrap sx={{ fontSize: 14, fontWeight: 600, lineHeight: 1.4, m: 0 }}>{row.headName || row.gradeName || row.employee?.name || row.name || row._id}</Typography>
            {row.isDefault && tab !== 'headType' && (
              <Chip size="small" label="Default" style={{ borderRadius: 5, fontSize: 10, height: 18, color: '#1d4ed8', backgroundColor: '#dbeafe' }} />
            )}
          </Box>
        </Tooltip>
      )
    },
    {
      field: 'type',
      headerName: tab === 'employeeSalaryConfig' ? 'Pay Type' : 'Value',
      flex: 1,
      valueGetter: ({ row }: GridValueGetterParams) => {
        if (row.gradeValue !== undefined) {
          return row.gradeValue || 0;
        }
        return row.componentType || row.type || row.salaryType || row.salryType || row.headType?.name || '-';
      },
      renderCell: ({ row }: GridValueGetterParams) => {
        if (row.gradeValue !== undefined) {
          return `₹${Number(row.gradeValue || 0).toLocaleString('en-IN')}`;
        }
        return row.componentType || row.type || row.salaryType || row.salryType || row.headType?.name || '-';
      }
    },
    ...(tab === 'employeeSalaryConfig' ? [
      {
        field: 'monthlyCtc',
        headerName: 'Monthly',
        flex: 0.9,
        renderCell: ({ row }: GridValueGetterParams) => {
          const annual = getDecimalValue(row.ctcAmount || row.salaryGrade?.gradeValue);
          const monthly = annual ? annual / 12 : totalAmount((row.companyAllowanceHeads || []).map((item: any) => ({ value: String(getDecimalValue(item.value)) })));
          return (
            <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
              {formatCurrency(monthly)}
            </Typography>
          );
        }
      },
      {
        field: 'annualCtc',
        headerName: 'Annual CTC',
        flex: 0.9,
        renderCell: ({ row }: GridValueGetterParams) => {
          const annual = getDecimalValue(row.ctcAmount || row.salaryGrade?.gradeValue);
          return (
            <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
              {formatCurrency(annual)}
            </Typography>
          );
        }
      },
      {
        field: 'heads',
        headerName: 'Salary Heads',
        flex: 1.5,
        renderCell: ({ row }: GridValueGetterParams) => {
          const earnings = row.companyAllowanceHeads || [];
          const deductions = row.deductionsHeads || [];
          if (!earnings.length && !deductions.length) {
            return <Typography sx={{ fontSize: 12, color: '#9da7b7' }}>No heads</Typography>;
          }
          return (
            <Typography sx={{ fontSize: 12, color: '#374151', whiteSpace: 'normal', lineHeight: 1.35 }}>
              {earnings.length} earning(s) {deductions.length ? ` / ${deductions.length} deduction(s)` : ''}
            </Typography>
          );
        }
      }
    ] as GridColDef[] : []),
    {
      field: 'isActive',
      headerName: 'Status',
      flex: 0.7,
      renderCell: ({ row }: GridValueGetterParams) => (
        <Chip
          size="small"
          label={row.isActive !== false ? 'Active' : 'Inactive'}
          style={{ borderRadius: 7, color: '#000', backgroundColor: row.isActive !== false ? '#e8f5e9' : '#eeeeee' }}
        />
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.9,
      sortable: false,
      renderCell: ({ row }: GridValueGetterParams) => {
        if (tab === 'headType' && row.isDefault) {
          return (
            <Chip
              size="small"
              label="System Default"
              style={{ borderRadius: 7, color: '#6b7280', backgroundColor: '#f3f4f6', fontSize: 11 }}
            />
          );
        }
        return (
          <Stack direction="row" spacing={1}>
            <Tooltip title="Edit">
              <IconButton size="small" onClick={() => openEditForm(row)} style={actionButtonStyle}>
                <EditIcon style={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton size="small" onClick={() => setDeleteTarget(row)} style={deleteButtonStyle}>
                <DeleteIcon style={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Stack>
        );
      }
    }
  ], [tab, rows]);

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      fontSize: '0.875rem',
      height: 35,
      backgroundColor: '#fff',
      display: 'flex',
      alignItems: 'center',
      transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
      '& fieldset': { borderColor: '#9da7b7', borderWidth: '1px' },
      '&:hover fieldset': { borderColor: '#94a3b8' },
      '&.Mui-focused fieldset': { borderColor: '#0f172a', borderWidth: '1.5px' },
      '&.Mui-focused': { boxShadow: '0 0 0 2px rgba(15, 23, 42, 0.05)' },
      '&.Mui-disabled': { backgroundColor: '#f8fafc', '& fieldset': { borderColor: '#9da7b7' } },
    },
    '& .MuiOutlinedInput-input': {
      padding: '0 10px !important',
      height: '35px !important',
      lineHeight: '35px !important',
      boxSizing: 'border-box',
      fontSize: '0.875rem',
      color: '#0f172a',
      display: 'flex',
      alignItems: 'center',
      '&::placeholder': { color: '#94a3b8', opacity: 1 },
    },
    '& .MuiAutocomplete-inputRoot': {
      padding: '0 10px !important',
      height: 35,
      minHeight: 35,
      alignItems: 'center',
    },
    '& .MuiAutocomplete-input': {
      padding: '0 !important',
      height: '33px !important',
      lineHeight: '33px !important',
    },
    '& input[type="date"]': {
      lineHeight: '35px',
      width: '100%',
      display: 'flex',
      justifyContent: 'space-between',
      '&::-webkit-datetime-edit': { flex: 1 },
      '&::-webkit-calendar-picker-indicator': {
        cursor: 'pointer',
        marginLeft: 'auto',
        opacity: 0.6,
      },
      '&::-webkit-calendar-picker-indicator:hover': {
        opacity: 1,
      },
    },
    '& .Mui-disabled .MuiOutlinedInput-input': { color: '#64748b', WebkitTextFillColor: '#64748b' },
    '& textarea.MuiOutlinedInput-input': {
      height: 'auto',
      lineHeight: 1.5,
    },
  };

  const selectSx = {
    ...fieldSx,
    '& .MuiSelect-select': {
      height: '35px !important',
      minHeight: '35px !important',
      padding: '0 28px 0 10px',
      display: 'flex',
      alignItems: 'center',
      fontSize: '0.875rem',
      boxSizing: 'border-box',
    },
  };

  const FieldLabel = ({ children }: { children: React.ReactNode }) => (
    <h5 className='c-label' style={{ margin: '0 0 4px' }}>{children}</h5>
  );

  const getDrawerTitle = () => {
    if (tab === 'salaryHead') {
      const subLabel = salaryHeadSubTabConfig.find(s => s.key === salaryHeadSubTab)?.label.replace(/s$/, '') || 'Salary Head';
      return salaryHeadForm._id ? `Edit ${subLabel}` : `Add ${subLabel}`;
    }
    if (tab === 'salaryGrade') return salaryGradeForm._id ? 'Edit Salary Grade' : 'Add Salary Grade';
    if (tab === 'loan') return loanForm._id ? 'Edit Loan' : 'Add Loan';
    return employeeSalaryConfigForm._id ? 'Edit Salary Config' : 'Add Salary Config';
  };

  const handleDrawerSave = () => {
    if (tab === 'salaryHead') return handleSaveSalaryHead();
    if (tab === 'salaryGrade') return handleSaveSalaryGrade();
    if (tab === 'loan') return handleSaveLoan();
    return handleSaveEmployeeSalaryConfig();
  };

  return (
    <Box sx={{ overflowX: 'hidden', width: '100%' }}>
      <Box sx={{
        position: 'sticky',
        top: 0,
        zIndex: 10,
        borderBottom: 1,
        borderColor: 'divider',
        backgroundColor: 'background.default'
      }}>
        {/* same look as the Inventory tabs */}
        <Tabs
          value={activeTabIndex}
          onChange={(_, index) => {
            setTab(tabConfig[index].key);
            setSearchInput('');
            setPage(0);
          }}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{ '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 } }}
        >
          {tabConfig.map(item => (
            <Tab key={item.key} label={item.label} />
          ))}
        </Tabs>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", width: '100%', pt: 2, pb: 1, gap: 1.5 }}>
        {tab === 'salarySetup' && !salarySetupLoading && !salarySetupDocId && (
          <Box sx={{
            minHeight: 'calc(100vh - 260px)', display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', bgcolor: '#f9fafb',
            border: '1px dashed #d1d5db', borderRadius: 2, p: 4
          }}>
            <Box sx={{
              width: 88, height: 88, borderRadius: '50%', bgcolor: '#dcfce7',
              display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2
            }}>
              <AddIcon sx={{ fontSize: 44, color: '#059669' }} />
            </Box>
            <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#111827', mb: 0.5 }}>
              No Salary Setup Yet
            </Typography>
            <Typography sx={{ fontSize: 13, color: '#6b7280', maxWidth: 420, textAlign: 'center', mb: 2.5 }}>
              Add the standard allowance and deduction heads in one click. You can remove or add heads afterwards.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              disabled={salarySetupSaving}
              onClick={applyDefaultSalarySetup}
              sx={{ textTransform: 'none', height: 40, px: 3, fontSize: 14, fontWeight: 600, bgcolor: '#059669', '&:hover': { bgcolor: '#047857' } }}
            >
              {salarySetupSaving ? 'Setting up...' : 'Default Setup'}
            </Button>
          </Box>
        )}
        {tab === 'salarySetup' && (salarySetupLoading || !!salarySetupDocId) && (
          <Box sx={{ bgcolor: '#fff', border: '1px solid #e5e7eb', borderRadius: 2, p: 2, fontFamily: 'inherit' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
              <Box>
                <Typography sx={{ fontSize: 16, fontWeight: 500, color: '#000', fontFamily: 'inherit' }}>Salary Setup</Typography>
                <Typography sx={{ fontSize: 14, color: '#9da7b7', lineHeight: 1.5, fontWeight: 400, fontFamily: 'inherit' }}>
                  Select default allowance and deduction heads. These will auto-populate in Employee Salary Config.
                </Typography>
              </Box>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                {salarySetupSaving && (
                  <Typography sx={{ fontSize: 14, color: '#9da7b7', fontStyle: 'italic', fontFamily: 'inherit' }}>Saving...</Typography>
                )}
                <Button
                  size="small"
                  variant="contained"
                  disableElevation
                  startIcon={<AddIcon />}
                  disabled={salarySetupSaving || salarySetupLoading}
                  onClick={applyDefaultSalarySetup}
                  sx={{ textTransform: 'none', fontFamily: 'inherit', borderRadius: 2, bgcolor: '#059669', '&:hover': { bgcolor: '#047857' } }}
                >
                  Default Setup
                </Button>
              </Stack>
            </Stack>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Box sx={{ border: '1px solid #e5e7eb', borderRadius: 2, overflow: 'hidden' }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 1.5, py: 1, bgcolor: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>
                    <Typography sx={{ fontSize: 15, fontWeight: 500, color: '#000', fontFamily: 'inherit' }}>
                      Allowance Heads
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() => { setAddHeadSelected(null); setAddHeadDialog({ open: true, section: 'allowance' }); }}
                      sx={{ textTransform: 'none', height: 32, fontSize: 14, fontFamily: 'inherit', fontWeight: 500 }}
                    >
                      Add
                    </Button>
                  </Stack>
                  <Box sx={{ maxHeight: 360, overflowY: 'auto' }}>
                    {salarySetupLoading ? (
                      <Typography sx={{ fontSize: 14, color: '#9da7b7', textAlign: 'center', py: 3, fontFamily: 'inherit' }}>Loading...</Typography>
                    ) : salarySetupAllowanceIds.length === 0 ? (
                      <Typography sx={{ fontSize: 14, color: '#9da7b7', textAlign: 'center', py: 3, fontFamily: 'inherit' }}>No allowance heads added. Click Add.</Typography>
                    ) : (
                      <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                        <Box component="thead" sx={{ bgcolor: '#fafafa' }}>
                          <Box component="tr">
                            <Box component="th" sx={{ textAlign: 'left', fontSize: 13, fontWeight: 500, color: '#9da7b7', px: 1.5, py: 1, borderBottom: '1px solid #e5e7eb', fontFamily: 'inherit' }}>Head Name</Box>
                            <Box component="th" sx={{ textAlign: 'left', fontSize: 13, fontWeight: 500, color: '#9da7b7', px: 1.5, py: 1, borderBottom: '1px solid #e5e7eb', fontFamily: 'inherit' }}>Type</Box>
                            <Box component="th" sx={{ textAlign: 'left', fontSize: 13, fontWeight: 500, color: '#9da7b7', px: 1.5, py: 1, borderBottom: '1px solid #e5e7eb', fontFamily: 'inherit' }}>Calculation Type</Box>
                            <Box component="th" sx={{ textAlign: 'left', fontSize: 13, fontWeight: 500, color: '#9da7b7', px: 1.5, py: 1, borderBottom: '1px solid #e5e7eb', fontFamily: 'inherit' }}>Value</Box>
                            <Box component="th" sx={{ width: 40, borderBottom: '1px solid #e5e7eb' }} />
                          </Box>
                        </Box>
                        <Box component="tbody">
                          {salarySetupAllowanceIds.map(id => {
                            const h = salaryHeads.find((x: any) => String(x._id) === id);
                            if (!h) return null;
                            return (
                              <Box component="tr" key={id} sx={{ '&:hover': { bgcolor: '#f9fafb' } }}>
                                <Box component="td" sx={{ fontSize: 14, color: '#000', px: 1.5, py: 1, borderBottom: '1px solid #f3f4f6', fontFamily: 'inherit', fontWeight: 400 }}>{h.headName}</Box>
                                <Box component="td" sx={{ fontSize: 14, color: '#000', px: 1.5, py: 1, borderBottom: '1px solid #f3f4f6', fontFamily: 'inherit' }}>{h.headType?.name || h.componentType || '-'}</Box>
                                <Box component="td" sx={{ fontSize: 14, color: '#000', px: 1.5, py: 1, borderBottom: '1px solid #f3f4f6', fontFamily: 'inherit' }}>{calculationTypeLabel(h.calculationType)}</Box>
                                <Box component="td" sx={{ fontSize: 14, color: '#000', px: 1.5, py: 1, borderBottom: '1px solid #f3f4f6', fontFamily: 'inherit' }}>
                                  {h.calculationType === 'Fixed Amount' ? `₹${Number(h.defaultAmount || 0).toLocaleString('en-IN')}` :
                                   h.calculationType?.includes('Percentage') ? `${h.percentage || 0}%` : (h.calculationType || '-')}
                                </Box>
                                <Box component="td" sx={{ px: 0.5, py: 0.4, borderBottom: '1px solid #f3f4f6', textAlign: 'center' }}>
                                  <IconButton size="small" onClick={() => removeSetupHead('allowance', id)}>
                                    <DeleteIcon sx={{ fontSize: 14, color: '#ef4444' }} />
                                  </IconButton>
                                </Box>
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ border: '1px solid #e5e7eb', borderRadius: 2, overflow: 'hidden' }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 1.5, py: 1, bgcolor: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>
                    <Typography sx={{ fontSize: 15, fontWeight: 500, color: '#000', fontFamily: 'inherit' }}>
                      Deduction Heads
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() => { setAddHeadSelected(null); setAddHeadDialog({ open: true, section: 'deduction' }); }}
                      sx={{ textTransform: 'none', height: 32, fontSize: 14, fontFamily: 'inherit', fontWeight: 500 }}
                    >
                      Add
                    </Button>
                  </Stack>
                  <Box sx={{ maxHeight: 360, overflowY: 'auto' }}>
                    {salarySetupLoading ? (
                      <Typography sx={{ fontSize: 14, color: '#9da7b7', textAlign: 'center', py: 3, fontFamily: 'inherit' }}>Loading...</Typography>
                    ) : salarySetupDeductionIds.length === 0 ? (
                      <Typography sx={{ fontSize: 14, color: '#9da7b7', textAlign: 'center', py: 3, fontFamily: 'inherit' }}>No deduction heads added. Click Add.</Typography>
                    ) : (
                      <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                        <Box component="thead" sx={{ bgcolor: '#fafafa' }}>
                          <Box component="tr">
                            <Box component="th" sx={{ textAlign: 'left', fontSize: 13, fontWeight: 500, color: '#9da7b7', px: 1.5, py: 1, borderBottom: '1px solid #e5e7eb', fontFamily: 'inherit' }}>Head Name</Box>
                            <Box component="th" sx={{ textAlign: 'left', fontSize: 13, fontWeight: 500, color: '#9da7b7', px: 1.5, py: 1, borderBottom: '1px solid #e5e7eb', fontFamily: 'inherit' }}>Type</Box>
                            <Box component="th" sx={{ textAlign: 'left', fontSize: 13, fontWeight: 500, color: '#9da7b7', px: 1.5, py: 1, borderBottom: '1px solid #e5e7eb', fontFamily: 'inherit' }}>Calculation Type</Box>
                            <Box component="th" sx={{ textAlign: 'left', fontSize: 13, fontWeight: 500, color: '#9da7b7', px: 1.5, py: 1, borderBottom: '1px solid #e5e7eb', fontFamily: 'inherit' }}>Calc</Box>
                            <Box component="th" sx={{ width: 40, borderBottom: '1px solid #e5e7eb' }} />
                          </Box>
                        </Box>
                        <Box component="tbody">
                          {salarySetupDeductionIds.map(id => {
                            const h = salaryHeads.find((x: any) => String(x._id) === id);
                            if (!h) return null;
                            return (
                              <Box component="tr" key={id} sx={{ '&:hover': { bgcolor: '#f9fafb' } }}>
                                <Box component="td" sx={{ fontSize: 14, color: '#000', px: 1.5, py: 1, borderBottom: '1px solid #f3f4f6', fontFamily: 'inherit', fontWeight: 400 }}>{h.headName}</Box>
                                <Box component="td" sx={{ fontSize: 14, color: '#000', px: 1.5, py: 1, borderBottom: '1px solid #f3f4f6', fontFamily: 'inherit' }}>{h.headType?.name || h.componentType || '-'}</Box>
                                <Box component="td" sx={{ fontSize: 14, color: '#000', px: 1.5, py: 1, borderBottom: '1px solid #f3f4f6', fontFamily: 'inherit' }}>{calculationTypeLabel(h.calculationType)}</Box>
                                <Box component="td" sx={{ fontSize: 14, color: '#000', px: 1.5, py: 1, borderBottom: '1px solid #f3f4f6', fontFamily: 'inherit' }}>
                                  {h.calculationType === 'Fixed Amount' ? `₹${Number(h.defaultAmount || 0).toLocaleString('en-IN')}` :
                                   h.calculationType?.includes('Percentage') ? `${h.percentage || 0}%` : (h.calculationType || '-')}
                                </Box>
                                <Box component="td" sx={{ px: 0.5, py: 0.4, borderBottom: '1px solid #f3f4f6', textAlign: 'center' }}>
                                  <IconButton size="small" onClick={() => removeSetupHead('deduction', id)}>
                                    <DeleteIcon sx={{ fontSize: 14, color: '#ef4444' }} />
                                  </IconButton>
                                </Box>
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}

        <Dialog open={addHeadDialog.open} onClose={() => setAddHeadDialog({ open: false, section: addHeadDialog.section })} maxWidth="sm" fullWidth>
          <DialogTitle sx={greyDialogTitleSx}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="h5">Add Salary Head</Typography>
                <Typography variant="body2">Pick allowance and deduction heads to add to the salary setup</Typography>
              </Box>
              <IconButton size="small" onClick={() => { setAddHeadSelected(null); setAddHeadDialog({ open: false, section: addHeadDialog.section }); }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent dividers sx={{ p: 0 }}>
            <Box sx={{ px: 2, py: 1, borderBottom: '1px solid #e5e7eb', bgcolor: '#f8fafc' }}>
              <TextField
                fullWidth size="small" placeholder="Search head..." autoFocus
                value={addHeadSelected?.__search || ''}
                onChange={(e) => setAddHeadSelected({ __search: e.target.value })}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon size={16} /></InputAdornment>,
                }}
              />
            </Box>
            <Box sx={{ maxHeight: 420, overflowY: 'auto' }}>
              {(() => {
                const q = String(addHeadSelected?.__search || '').toLowerCase().trim();
                const list = salaryHeads;
                const filtered = q ? list.filter((h: any) =>
                  String(h.headName || '').toLowerCase().includes(q)
                  || String(h.headType?.name || '').toLowerCase().includes(q)
                  || String(h.componentType || '').toLowerCase().includes(q)
                ) : list;
                if (!filtered.length) {
                  return <Typography sx={{ fontSize: 12, color: '#9ca3af', textAlign: 'center', py: 4 }}>No heads found</Typography>;
                }
                return filtered.map((h: any) => {
                  const id = String(h._id);
                  const isAllow = ['Earning', 'Reimbursement'].includes(h.componentType || 'Earning');
                  const targetSection: 'allowance' | 'deduction' = isAllow ? 'allowance' : 'deduction';
                  const existingIds = targetSection === 'allowance' ? salarySetupAllowanceIds : salarySetupDeductionIds;
                  const exists = existingIds.includes(id);
                  return (
                    <Box
                      key={id}
                      onClick={() => {
                        if (exists) return;
                        addSetupHead(targetSection, id);
                        setAddHeadSelected(null);
                        setAddHeadDialog({ open: false, section: addHeadDialog.section });
                      }}
                      sx={{
                        px: 2, py: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        borderBottom: '1px solid #f3f4f6',
                        cursor: exists ? 'default' : 'pointer',
                        opacity: exists ? 0.55 : 1,
                        '&:hover': { bgcolor: exists ? 'transparent' : '#f9fafb' }
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography sx={{ fontSize: 13, fontWeight: 500, color: '#111827' }}>{h.headName}</Typography>
                          <Chip
                            size="small"
                            label={isAllow ? 'Allowance' : 'Deduction'}
                            style={{
                              borderRadius: 6, fontSize: 10, height: 18,
                              color: isAllow ? '#1d4ed8' : '#b91c1c',
                              backgroundColor: isAllow ? '#dbeafe' : '#fee2e2'
                            }}
                          />
                        </Stack>
                        <Typography sx={{ fontSize: 11, color: '#9ca3af' }}>
                          {h.headType?.name || h.componentType || '-'}
                          {h.calculationType === 'Fixed Amount' ? ` · ₹${Number(h.defaultAmount || 0).toLocaleString('en-IN')}` :
                           h.calculationType?.includes('Percentage') ? ` · ${h.percentage || 0}%` : ''}
                        </Typography>
                      </Box>
                      {exists ? (
                        <Chip size="small" label="Added" style={{ borderRadius: 6, fontSize: 10, height: 20, color: '#15803d', backgroundColor: '#dcfce7' }} />
                      ) : (
                        <Button size="small" startIcon={<AddIcon />} sx={{ textTransform: 'none', fontSize: 12, height: 26 }}>Add</Button>
                      )}
                    </Box>
                  );
                });
              })()}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button variant='outlined' onClick={() => { setAddHeadSelected(null); setAddHeadDialog({ open: false, section: addHeadDialog.section }); }} sx={{ textTransform: 'none' }}>Close</Button>
          </DialogActions>
        </Dialog>

        {tab !== 'salarySetup' && tab === 'salaryHead' && (
          <Box sx={{ borderBottom: '1px solid divider', mb: 1 }}>
            <Tabs
              value={salaryHeadSubTabConfig.findIndex(s => s.key === salaryHeadSubTab)}
              onChange={(_, idx) => { setSalaryHeadSubTab(salaryHeadSubTabConfig[idx].key); setPage(0); }}
              sx={{
                minHeight: 40,
                '& .MuiTab-root': { textTransform: 'none', minHeight: 40, fontWeight: 600 },
                '& .MuiTabs-indicator': { bgcolor: '#059669', height: 2 }
              }}
            >
              {salaryHeadSubTabConfig.map(s => <Tab key={s.key} label={s.label} />)}
            </Tabs>
          </Box>
        )}
        {tab !== 'salarySetup' && (<>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: '100%', gap: 2, pl: '4px', pr: '20px', boxSizing: 'border-box' }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 1 }}>
                <TextField
                  className='c-text-field-3'
                  variant="standard"
                  style={{ width: 300, float: 'left' }}
                  size='small'
                  placeholder={`Search ${activeTab.label}...`}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  InputProps={{
                    disableUnderline: true,
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon style={{ marginTop: -3 }} size={20} />
                      </InputAdornment>
                    ),
                    style: { fontSize: 14, fontWeight: 'normal' },
                  }}
                />
              </Box>
              <Stack direction="row" spacing={1}>
          {tab !== 'headType' && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openCreateForm}
              sx={{ height: 36, textTransform: 'none' }}
            >
              {tab === 'salaryHead'
                ? `Add ${salaryHeadSubTabConfig.find(s => s.key === salaryHeadSubTab)?.label.replace(/s$/, '')}`
                : `Add ${activeTab.label.replace(/s$/, '')}`}
            </Button>
          )}
        </Stack>
      </Box>

      {!loading && filteredRows.length === 0 && !searchInput ? (
        <Box sx={{
          minHeight: 'calc(100vh - 260px)', display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', bgcolor: '#f9fafb',
          border: '1px dashed #d1d5db', borderRadius: 2, p: 4
        }}>
          <Box sx={{
            width: 88, height: 88, borderRadius: '50%', bgcolor: '#dcfce7',
            display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2
          }}>
            <AddIcon sx={{ fontSize: 44, color: '#059669' }} />
          </Box>
          <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#111827', mb: 0.5 }}>
            No {activeTab.label} Yet
          </Typography>
          <Typography sx={{ fontSize: 13, color: '#6b7280', maxWidth: 420, textAlign: 'center', mb: 2.5 }}>
            {tab === 'employeeSalaryConfig' && 'Start configuring salaries by adding your first employee salary config. You can set CTC, allowances, deductions and more.'}
            {tab === 'salaryGrade' && 'Define salary grades for your organization. Group employees by salary band and auto-apply structures.'}
            {tab === 'loan' && 'Track employee loans and advances. Manage EMIs, repayment schedules and balances here.'}
            {tab === 'salaryHead' && 'Add salary head components like Basic, HRA, PF etc. used while building salary structures.'}
            {tab === 'headType' && 'Create salary head categories (Allowance, Deduction, Reimbursement, etc.) to group your heads.'}
          </Typography>
          {tab !== 'headType' && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openCreateForm}
              sx={{ textTransform: 'none', height: 40, px: 3, fontSize: 14, fontWeight: 600 }}
            >
              Create Your First {activeTab.label.replace(/s$/, '')}
            </Button>
          )}
        </Box>
      ) : (
        <Box sx={{ height: 'calc(100vh - 180px)', minHeight: 500, width: '100%', pr: '10px', boxSizing: 'border-box' }}>
          <CustomDataGrid
            columns={tab === 'salaryHead' ? salaryHeadColumns : tab === 'loan' ? loanColumns : setupColumns}
            rows={filteredRows}
            getRowId={(row: any) => row._id}
            loading={loading}
            checkboxSelection={false}
            disableSelectionOnClick
            rowCount={filteredRows.length}
            page={page}
            pageSize={pageSize}
            onPageChange={(newPage) => setPage(newPage)}
            onPageSizeChange={(newPageSize) => {
              setPageSize(newPageSize);
              setPage(0);
            }}
            rowsPerPageOptions={defaultRowsPerPageOptions}
          />
        </Box>
      )}
      </>)}
      </Box>

      <Modal
        open={drawerOpen}
        onClose={(_e: any, reason: any) => {
          if (reason !== 'backdropClick') setDrawerOpen(false);
        }}
      >
        <Box
          sx={tab === 'loan' ? {
            position: 'absolute',
            top: 0,
            right: 0,
            width: tab === 'loan' ? 680 : 500,
            maxWidth: '95vw',
            height: '100vh',
            bgcolor: 'background.paper',
            borderRadius: 0,
            border: 'none',
            boxShadow: 24,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          } : {
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: tab === 'salaryHead' ? 1000 : tab === 'employeeSalaryConfig' ? 900 : tab === 'salaryGrade' ? 600 : 800,
            maxWidth: '95vw',
            maxHeight: '95vh',
            bgcolor: 'background.paper',
            borderRadius: 2,
            border: 'none',
            boxShadow: 24,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {tab === 'loan' ? (
            <DialogTitle sx={greyDialogTitleSx}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", minHeight: '40px' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem' }}>
                  {getDrawerTitle()}
                </Typography>
              </Box>
            </DialogTitle>
          ) : (tab === 'salaryHead' || tab === 'salaryGrade') ? (
            <DialogTitle sx={greyDialogTitleSx}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h5">{getDrawerTitle()}</Typography>
                  <Typography variant="body2">{tab === 'salaryGrade' ? 'Set the grade name and value' : 'Set the head type, calculation and payroll settings'}</Typography>
                </Box>
                <IconButton size="small" onClick={() => setDrawerOpen(false)}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </DialogTitle>
          ) : (
          <Grid container>
            <Grid {...{ xs: 12, sm: 8, md: 4, lg: 12, p: 2, pl: 2, bgcolor: '#e9ecf1' }} style={{ borderTopLeftRadius: 8, borderTopRightRadius: 8, height: 50 }}>
              {(
                <div style={{ float: 'right', marginTop: -7, marginRight: -5 }}>
                  <Button
                    onClick={handleDrawerSave}
                    disabled={saving}
                    variant="contained"
                    size="small"
                    style={{ color: '#488dff', backgroundColor: '#fff', border: '1px solid #488dff', marginRight: 10 }}
                  >
                    {saving ? 'Saving...' : (salaryHeadForm._id || salaryGradeForm._id || loanForm._id || employeeSalaryConfigForm._id ? 'Update' : 'Create')}
                  </Button>
                  <Button
                    onClick={() => setDrawerOpen(false)}
                    variant="contained"
                    size="small"
                    style={{ color: '#488dff', backgroundColor: '#fff', border: '1px solid #488dff', marginRight: 10 }}
                  >
                    Cancel
                  </Button>
                </div>
              )}
              <h3 style={{ marginTop: -2 }}>{getDrawerTitle()}</h3>
            </Grid>
          </Grid>
          )}

          <Box sx={{ overflow: 'auto', flex: 1, maxHeight: 'calc(95vh - 50px)' }}>
          <Grid container columnSpacing={2} rowSpacing={1.4} sx={{ p: 2.5 }}>
            {tab === 'salaryHead' && (
              <>
                <Grid item xs={12} sm={6}>
                  <FieldLabel>Head Type *</FieldLabel>
                  <TextField
                    fullWidth
                    size="small"
                    select
                    disabled={isDefaultHeadEdit}
                    sx={fieldSx}
                    value={salaryHeadForm.headType || ''}
                    onChange={(e) => {
                      if (e.target.value === '__ADD_NEW__') {
                        setHeadTypeDialogOpen(true);
                        return;
                      }
                      setSalaryHeadForm(prev => ({ ...prev, headType: e.target.value } as any));
                    }}
                    SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 320 } } } }}
                  >
                    {headTypes.map((t: any) => (
                      <MenuItem key={t._id} value={t._id}>{t.name}</MenuItem>
                    ))}
                    <MenuItem value="__ADD_NEW__" sx={{ color: '#059669', fontWeight: 600, borderTop: '1px solid #e5e7eb' }}>
                      + Add new Head Type
                    </MenuItem>
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FieldLabel>Name *</FieldLabel>
                  <TextField fullWidth size="small" sx={fieldSx} disabled={isDefaultHeadEdit} value={salaryHeadForm.headName} onChange={(e) => setSalaryHeadForm(prev => ({ ...prev, headName: e.target.value }))} />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FieldLabel>Alias</FieldLabel>
                  <TextField fullWidth size="small" sx={fieldSx} disabled={isDefaultHeadEdit} value={salaryHeadForm.alias} onChange={(e) => setSalaryHeadForm(prev => ({ ...prev, alias: e.target.value }))} />
                </Grid>

                {salaryHeadForm.calculationType !== 'Manual' && salaryHeadForm.calculationType !== 'Balance' && <Grid item xs={12} sm={6}>
                  <FieldLabel>{salaryHeadForm.calculationType.includes('Percentage') ? 'Percentage' : 'Amount'}</FieldLabel>
                  <TextField
                    fullWidth
                    size="small"
                    sx={fieldSx}
                    type="number"
                    value={salaryHeadForm.calculationType.includes('Percentage') ? salaryHeadForm.percentage : salaryHeadForm.defaultAmount}
                    onChange={(e) => setSalaryHeadForm(prev => salaryHeadForm.calculationType.includes('Percentage')
                      ? { ...prev, percentage: e.target.value }
                      : { ...prev, defaultAmount: e.target.value })}
                    InputProps={{
                      startAdornment: !salaryHeadForm.calculationType.includes('Percentage') ? <Typography sx={{ mr: 1 }}>₹</Typography> : null,
                      endAdornment: salaryHeadForm.calculationType.includes('Percentage') ? <Typography sx={{ ml: 1 }}>%</Typography> : null
                    }}
                  />
                </Grid>}

                <Grid item xs={12}>
                  <FieldLabel>Calculation Type *</FieldLabel>
                  <RadioGroup
                    row
                    value={salaryHeadForm.calculationType}
                    onChange={(e) => setSalaryHeadForm(prev => ({ ...prev, calculationType: e.target.value }))}
                    sx={{ gap: 1.5 }}
                  >
                    <FormControlLabel value="Fixed Amount" control={<Radio size="small" />} label={<Typography>Flat Amount</Typography>} />
                    <FormControlLabel value="Percentage of Basic" control={<Radio size="small" />} label={<Typography>% of Basic</Typography>} />
                    <FormControlLabel value="Percentage of CTC" control={<Radio size="small" />} label={<Typography>% of CTC</Typography>} />
                    <FormControlLabel value="Percentage of Gross" control={<Radio size="small" />} label={<Typography>% of Gross</Typography>} />
                    {(() => {
                      const balanceTaken = salaryHeads.some(
                        (h: any) => h.calculationType === 'Balance' && String(h._id) !== String(salaryHeadForm._id)
                      );
                      return (
                        <FormControlLabel
                          value="Balance"
                          control={<Radio size="small" />}
                          disabled={balanceTaken}
                          label={
                            <Typography>
                              Balance (Remaining){balanceTaken ? ' — already in use' : ''}
                            </Typography>
                          }
                        />
                      );
                    })()}
                    <FormControlLabel value="Manual" control={<Radio size="small" />} label={<Typography>Manual</Typography>} />
                  </RadioGroup>
                </Grid>

                <Grid item xs={12}><Divider sx={{ my: 0.5 }} /></Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ color: '#111827', mb: 1 }}>Configurations</Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={12} sm={6}>
                      <FormControlLabel
                        sx={{ ml: 0 }}
                        control={<Checkbox size="small" checked={salaryHeadForm.includeInCTC} onChange={(e) => setSalaryHeadForm(prev => ({ ...prev, includeInCTC: e.target.checked }))} />}
                        label={<Typography>Include in CTC</Typography>}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControlLabel
                        sx={{ ml: 0 }}
                        control={<Checkbox size="small" checked={salaryHeadForm.isTaxable} onChange={(e) => setSalaryHeadForm(prev => ({ ...prev, isTaxable: e.target.checked }))} />}
                        label={<Typography>Taxable</Typography>}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControlLabel
                        sx={{ ml: 0 }}
                        control={<Checkbox size="small" checked={salaryHeadForm.includeForPF} onChange={(e) => setSalaryHeadForm(prev => ({ ...prev, includeForPF: e.target.checked }))} />}
                        label={<Typography>EPF Contribution</Typography>}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControlLabel
                        sx={{ ml: 0 }}
                        control={<Checkbox size="small" checked={salaryHeadForm.includeForESI} onChange={(e) => setSalaryHeadForm(prev => ({ ...prev, includeForESI: e.target.checked }))} />}
                        label={<Typography>ESI Contribution</Typography>}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControlLabel
                        sx={{ ml: 0 }}
                        control={<Checkbox size="small" checked={salaryHeadForm.calculateOnProRata} onChange={(e) => setSalaryHeadForm(prev => ({ ...prev, calculateOnProRata: e.target.checked }))} />}
                        label={<Typography>Calculate Pro-rata</Typography>}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControlLabel
                        sx={{ ml: 0 }}
                        control={<Checkbox size="small" checked={salaryHeadForm.showInPayslip} onChange={(e) => setSalaryHeadForm(prev => ({ ...prev, showInPayslip: e.target.checked }))} />}
                        label={<Typography>Show in Payslip</Typography>}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControlLabel
                        sx={{ ml: 0 }}
                        control={<Checkbox size="small" checked={salaryHeadForm.isFlexibleBenefit} onChange={(e) => setSalaryHeadForm(prev => ({ ...prev, isFlexibleBenefit: e.target.checked }))} />}
                        label={<Typography>Flexible Benefit Plan</Typography>}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControlLabel
                        sx={{ ml: 0 }}
                        control={<Checkbox size="small" checked={salaryHeadForm.isActive !== false} onChange={(e) => setSalaryHeadForm(prev => ({ ...prev, isActive: e.target.checked } as any))} />}
                        label={<Typography>Active</Typography>}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </>
            )}

            {tab === 'salaryGrade' && (
              <>
                <Grid item xs={12} sm={6}>
                  <FieldLabel>Grade Name *</FieldLabel>
                  <TextField
                    fullWidth
                    size="small"
                    sx={fieldSx}
                    placeholder="e.g. P1, L2, Senior"
                    value={salaryGradeForm.gradeName}
                    onChange={(e) => setSalaryGradeForm(prev => ({ ...prev, gradeName: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FieldLabel>Grade Value</FieldLabel>
                  <TextField
                    fullWidth
                    size="small"
                    sx={fieldSx}
                    type="number"
                    placeholder="0"
                    value={salaryGradeForm.gradeValue}
                    onChange={(e) => handleAnnualGradeValueChange(e.target.value)}
                    InputProps={{
                      startAdornment: <span style={{ marginRight: 4, color: '#6b7280', fontSize: 14 }}>₹</span>
                    }}
                  />
                </Grid>
                {salaryGradeForm.gradeValue && Number(salaryGradeForm.gradeValue) > 0 && (
                  <Grid item xs={12}>
                    <Box sx={{ p: 1.5, bgcolor: '#eff6ff', borderRadius: 1, border: '1px solid #bfdbfe', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography sx={{ fontSize: 13, color: '#1e40af', fontWeight: 600 }}>Monthly Salary</Typography>
                      <Typography sx={{ fontSize: 14, color: '#1e3a8a', fontWeight: 700 }}>
                        ₹{Number(Number(salaryGradeForm.gradeValue) / 12).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </>
            )}

            {tab === 'employeeSalaryConfig' && (
              <>
                <Grid item xs={12} md={8}>
                  <Grid container columnSpacing={2} rowSpacing={1.4}>
                <Grid item xs={12}>
                  <FieldLabel>Employee</FieldLabel>
                  <Autocomplete
                    size="small"
                    options={salaryConfigEmployeeOptions}
                    value={employees.find((e: any) => e._id === employeeSalaryConfigForm.employee) || null}
                    onChange={(_, opt: any) => setEmployeeSalaryConfigForm(prev => ({ ...prev, employee: opt?._id || '' }))}
                    getOptionLabel={(opt: any) => `${opt?.name || 'Unnamed'} (${opt?.empId || '—'})`}
                    isOptionEqualToValue={(opt: any, val: any) => opt?._id === val?._id}
                    filterOptions={(opts, state) => {
                      const q = state.inputValue.trim().toLowerCase();
                      if (!q) return opts;
                      return opts.filter((e: any) =>
                        String(e.name || '').toLowerCase().includes(q) ||
                        String(e.empId || '').toLowerCase().includes(q) ||
                        String(e.personalEmail || e.businessEmail || '').toLowerCase().includes(q) ||
                        String(e.designation || '').toLowerCase().includes(q)
                      );
                    }}
                    renderOption={(props, opt: any) => (
                      <Box component="li" {...props} key={opt._id} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start !important', py: 0.8 }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{opt.name || 'Unnamed'}</Typography>
                        <Typography sx={{ fontSize: 11, color: '#6b7280' }}>
                          {opt.empId || '—'}{opt.designation ? ` · ${opt.designation}` : ''}
                        </Typography>
                      </Box>
                    )}
                    renderInput={(params) => (
                      <TextField {...params} placeholder="Type name, ID, email..." size="small" sx={fieldSx} />
                    )}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FieldLabel>Salary Grade</FieldLabel>
                  <FormControl fullWidth size="small" sx={selectSx}>
                    <Select
                      value={employeeSalaryConfigForm.salaryGrade}
                      onChange={(e) => handleGradeChange(e.target.value)}
                      displayEmpty
                      renderValue={(val) => val ? (salaryGrades.find((g: any) => g._id === val)?.gradeName || val) : <em style={{ color: '#9ca3af', fontStyle: 'normal' }}>Custom (No Grade)</em>}
                    >
                      <MenuItem value=""><em style={{ color: '#9ca3af' }}>Custom (No Grade)</em></MenuItem>
                      {salaryGrades.map(item => (
                        <MenuItem key={item._id} value={item._id}>{item.gradeName}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <FieldLabel>Salary Type</FieldLabel>
                  <FormControl fullWidth size="small" sx={selectSx}>
                    <Select value={employeeSalaryConfigForm.salaryType} onChange={(e) => setEmployeeSalaryConfigForm(prev => ({ ...prev, salaryType: e.target.value }))}>
                      <MenuItem value="Monthly">Monthly</MenuItem>
                      <MenuItem value="Daily">Daily</MenuItem>
                      <MenuItem value="Hourly">Hourly</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <FieldLabel>Effective From</FieldLabel>
                  <TextField
                    fullWidth
                    size="small"
                    sx={{
                      ...fieldSx,
                      '& input[type="date"]::-webkit-calendar-picker-indicator': { display: 'none' },
                      '& input[type="date"]::-webkit-inner-spin-button': { display: 'none' },
                      '& .MuiOutlinedInput-root': { ...fieldSx['& .MuiOutlinedInput-root'], height: 40 },
                      '& .MuiOutlinedInput-input': {
                        ...fieldSx['& .MuiOutlinedInput-input'],
                        height: '40px !important',
                        lineHeight: '40px !important',
                        paddingRight: '4px !important',
                      },
                    }}
                    type="date"
                    inputRef={effectiveFromInputRef}
                    value={employeeSalaryConfigForm.effectiveFrom}
                    onChange={(e) => setEmployeeSalaryConfigForm(prev => ({ ...prev, effectiveFrom: e.target.value }))}
                    InputProps={{
                      endAdornment: (
                        <IconButton
                          size="small"
                          onClick={() => {
                            const input = effectiveFromInputRef.current as any;
                            if (input?.showPicker) input.showPicker();
                            else effectiveFromInputRef.current?.focus();
                          }}
                          sx={{ color: '#64748b', p: 0.5, mr: -0.25 }}
                        >
                          <CalendarMonthIcon sx={{ fontSize: 21 }} />
                        </IconButton>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FieldLabel>Annual CTC</FieldLabel>
                  <TextField fullWidth size="small" sx={fieldSx} type="number" value={employeeSalaryConfigForm.ctcAmount}
                    onChange={(e) => handleCtcAmountChange(e.target.value)}
                    InputProps={{ startAdornment: <span style={{ marginRight: 4, color: '#6b7280', fontSize: 14 }}>₹</span> }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.6 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#1f2937' }}>Allowances</Typography>
                    <Button size="small" startIcon={<AddCircleOutlineIcon />} onClick={() => addSalaryConfigRow('earnings')} sx={{ textTransform: 'none', height: 30 }}>Add</Button>
                  </Box>
                </Grid>
                {employeeSalaryConfigForm.earnings.map((item, index) => (
                  <React.Fragment key={`earning-${index}`}>
                    <Grid item xs={5}>
                      <FieldLabel>{index === 0 ? 'Head' : ' '}</FieldLabel>
                      <Autocomplete
                        size="small"
                        options={salaryHeads}
                        value={salaryHeads.find(h => h.headName === item.head) || null}
                        onChange={(_, opt: any) => handleHeadSelect('earnings', index, opt?.headName || '')}
                        getOptionLabel={(opt: any) => opt.headName || ''}
                        renderOption={(props, opt: any) => (
                          <Box component="li" {...props} key={opt._id}>
                            <Box>
                              <Typography sx={{ fontSize: 13 }}>{opt.headName}</Typography>
                              {opt.headType?.name && <Typography sx={{ fontSize: 11, color: '#9ca3af' }}>{opt.headType.name}</Typography>}
                            </Box>
                          </Box>
                        )}
                        renderInput={(params) => <TextField {...params} placeholder="Search head..." size="small" sx={fieldSx} />}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <FieldLabel>{index === 0 ? '%' : ' '}</FieldLabel>
                      {(() => {
                        const head = salaryHeads.find(h => h.headName === item.head);
                        const masterPct = head?.percentage ? String(head.percentage) : '';
                        const masterAmt = head?.defaultAmount ? String(head.defaultAmount) : '';
                        const isUnlocked = unlockedPercentRows.has(`earnings-${index}`);
                        const displayValue = item.percent || masterPct || (masterAmt ? `₹${masterAmt}` : '');
                        return (
                          <TextField fullWidth size="small" sx={fieldSx}
                            type={isUnlocked ? 'number' : 'text'}
                            placeholder="0"
                            value={isUnlocked ? (item.percent || '') : displayValue}
                            disabled={!isUnlocked}
                            onChange={(e) => updateSalaryConfigRow('earnings', index, 'percent', e.target.value)}
                            InputProps={{
                              endAdornment: (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                                  {(item.percent || masterPct) && <span style={{ fontSize: 12, color: '#6b7280' }}>%</span>}
                                  <IconButton size="small" sx={{ p: 0.3 }} onClick={() => togglePercentLock(`earnings-${index}`)}>
                                    <EditIcon sx={{ fontSize: 14, color: isUnlocked ? '#059669' : '#9ca3af' }} />
                                  </IconButton>
                                </Box>
                              )
                            }}
                          />
                        );
                      })()}
                    </Grid>
                    <Grid item xs={3}>
                      <FieldLabel>{index === 0 ? 'Amount' : ' '}</FieldLabel>
                      <TextField fullWidth size="small" sx={fieldSx} type="number" value={item.value} onChange={(e) => updateSalaryConfigRow('earnings', index, 'value', e.target.value)}
                        InputProps={{ startAdornment: <span style={{ marginRight: 2, color: '#6b7280', fontSize: 13 }}>₹</span> }}
                      />
                    </Grid>
                    <Grid item xs={1} sx={{ display: 'flex', alignItems: 'flex-end' }}>
                      <IconButton size="small" onClick={() => removeSalaryConfigRow('earnings', index)} disabled={employeeSalaryConfigForm.earnings.length <= 1}>
                        <RemoveCircleOutlineIcon fontSize="small" />
                      </IconButton>
                    </Grid>
                  </React.Fragment>
                ))}

                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.6 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#1f2937' }}>Deductions</Typography>
                    <Button size="small" startIcon={<AddCircleOutlineIcon />} onClick={() => addSalaryConfigRow('deductions')} sx={{ textTransform: 'none', height: 30 }}>Add</Button>
                  </Box>
                </Grid>
                {employeeSalaryConfigForm.deductions.map((item, index) => (
                  <React.Fragment key={`deduction-${index}`}>
                    <Grid item xs={5}>
                      <FieldLabel>{index === 0 ? 'Head' : ' '}</FieldLabel>
                      <Autocomplete
                        size="small"
                        options={salaryHeads.filter(h => ['Deduction', 'Employer Contribution'].includes(h.componentType || ''))}
                        value={salaryHeads.find(h => h.headName === item.head) || null}
                        onChange={(_, opt: any) => handleHeadSelect('deductions', index, opt?.headName || '')}
                        getOptionLabel={(opt: any) => opt.headName || ''}
                        renderOption={(props, opt: any) => (
                          <Box component="li" {...props} key={opt._id}>
                            <Box>
                              <Typography sx={{ fontSize: 13 }}>{opt.headName}</Typography>
                              {opt.headType?.name && <Typography sx={{ fontSize: 11, color: '#9ca3af' }}>{opt.headType.name}</Typography>}
                            </Box>
                          </Box>
                        )}
                        renderInput={(params) => <TextField {...params} placeholder="Search head..." size="small" sx={fieldSx} />}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <FieldLabel>{index === 0 ? '%' : ' '}</FieldLabel>
                      {(() => {
                        const head = salaryHeads.find(h => h.headName === item.head);
                        const masterPct = head?.percentage ? String(head.percentage) : '';
                        const masterAmt = head?.defaultAmount ? String(head.defaultAmount) : '';
                        const isUnlocked = unlockedPercentRows.has(`deductions-${index}`);
                        const displayValue = item.percent || masterPct || (masterAmt ? `₹${masterAmt}` : '');
                        return (
                          <TextField fullWidth size="small" sx={fieldSx}
                            type={isUnlocked ? 'number' : 'text'}
                            placeholder="0"
                            value={isUnlocked ? (item.percent || '') : displayValue}
                            disabled={!isUnlocked}
                            onChange={(e) => updateSalaryConfigRow('deductions', index, 'percent', e.target.value)}
                            InputProps={{
                              endAdornment: (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                                  {(item.percent || masterPct) && <span style={{ fontSize: 12, color: '#6b7280' }}>%</span>}
                                  <IconButton size="small" sx={{ p: 0.3 }} onClick={() => togglePercentLock(`deductions-${index}`)}>
                                    <EditIcon sx={{ fontSize: 14, color: isUnlocked ? '#059669' : '#9ca3af' }} />
                                  </IconButton>
                                </Box>
                              )
                            }}
                          />
                        );
                      })()}
                    </Grid>
                    <Grid item xs={3}>
                      <FieldLabel>{index === 0 ? 'Amount' : ' '}</FieldLabel>
                      <TextField fullWidth size="small" sx={fieldSx} type="number" value={item.value} onChange={(e) => updateSalaryConfigRow('deductions', index, 'value', e.target.value)}
                        InputProps={{ startAdornment: <span style={{ marginRight: 2, color: '#6b7280', fontSize: 13 }}>₹</span> }}
                      />
                    </Grid>
                    <Grid item xs={1} sx={{ display: 'flex', alignItems: 'flex-end' }}>
                      <IconButton size="small" onClick={() => removeSalaryConfigRow('deductions', index)} disabled={employeeSalaryConfigForm.deductions.length <= 1}>
                        <RemoveCircleOutlineIcon fontSize="small" />
                      </IconButton>
                    </Grid>
                  </React.Fragment>
                ))}

                {employeeSalaryConfigForm.deductions.some(d => /^tds$/i.test(d.head)) && (
                  <Grid item xs={12}>
                    <Box sx={{ mt: 0.5, p: 1, bgcolor: '#fef3c7', border: '1px solid #fde68a', borderRadius: 1 }}>
                      <FieldLabel>TDS Rate</FieldLabel>
                      <FormControl fullWidth size="small" sx={selectSx}>
                        <Select
                          value={selectedTdsId}
                          onChange={(e) => {
                            const v = e.target.value;
                            setSelectedTdsId(v);
                            const opt = tdsRateOptions.find(o => o.value === v);
                            if (opt) {
                              const monthlyCtc = Number(employeeSalaryConfigForm.ctcAmount || 0) / 12;
                              const amt = monthlyCtc ? Number((monthlyCtc * Number(opt.rate) / 100).toFixed(2)) : 0;
                              setEmployeeSalaryConfigForm(prev => ({
                                ...prev,
                                deductions: prev.deductions.map(d => /^tds$/i.test(d.head)
                                  ? { ...d, percent: String(opt.rate), value: amt > 0 ? String(amt) : d.value }
                                  : d)
                              }));
                            }
                          }}
                          displayEmpty
                        >
                          <MenuItem value=""><em>Select TDS Rate / Section</em></MenuItem>
                          {tdsRateOptions.map((opt: any) => (
                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  </Grid>
                )}

                  </Grid>
                </Grid>

                <Grid item xs={12} md={4}>
                  {(() => {
                    const ctc = Number(employeeSalaryConfigForm.ctcAmount || 0);
                    const monthlyGross = ctc / 12;
                    const allocated = totalAmount(employeeSalaryConfigForm.earnings);
                    const unallocated = monthlyGross - allocated;
                    const allocPct = monthlyGross > 0 ? Math.min(100, (allocated / monthlyGross) * 100) : 0;
                    const totalDed = totalAmount(employeeSalaryConfigForm.deductions);
                    const netPay = allocated - totalDed;
                    const fmt = (n: number) => n.toLocaleString('en-IN', { maximumFractionDigits: 2 });
                    const isOver = unallocated < 0;
                    const isComplete = monthlyGross > 0 && Math.abs(unallocated) < 1;
                    return (
                      <Box sx={{ position: 'sticky', top: 0, display: 'flex', flexDirection: 'column', gap: 1.2 }}>
                      {monthlyGross > 0 && (
                        <Box sx={{
                          bgcolor: isOver ? '#fef2f2' : isComplete ? '#f0fdf4' : '#fffbeb',
                          border: `1px solid ${isOver ? '#fecaca' : isComplete ? '#bbf7d0' : '#fde68a'}`,
                          borderRadius: '10px',
                          p: 1.5
                        }}>
                          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.8 }}>
                            <Typography sx={{ fontSize: 10, fontWeight: 600, color: isOver ? '#991b1b' : isComplete ? '#15803d' : '#92400e', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                              {isOver ? 'Over-Allocated' : isComplete ? 'Fully Allocated' : 'Allocation Pending'}
                            </Typography>
                            <Typography sx={{ fontSize: 11, fontWeight: 700, color: isOver ? '#991b1b' : isComplete ? '#15803d' : '#92400e' }}>
                              {allocPct.toFixed(1)}%
                            </Typography>
                          </Stack>
                          <Typography sx={{ fontSize: 18, fontWeight: 700, color: isOver ? '#dc2626' : isComplete ? '#15803d' : '#d97706', mb: 0.4 }}>
                            ₹{fmt(Math.abs(unallocated))}
                          </Typography>
                          <Typography sx={{ fontSize: 10, color: isOver ? '#991b1b' : isComplete ? '#15803d' : '#92400e' }}>
                            {isOver ? 'Exceeds monthly gross. Reduce allowances.' : isComplete ? 'All salary distributed.' : 'Remaining to allocate'}
                          </Typography>
                          <Box sx={{ mt: 1, height: 6, bgcolor: '#fff', borderRadius: 3, overflow: 'hidden', border: '1px solid #e5e7eb' }}>
                            <Box sx={{
                              height: '100%',
                              width: `${Math.min(100, allocPct)}%`,
                              bgcolor: isOver ? '#dc2626' : isComplete ? '#15803d' : '#d97706',
                              transition: 'width 0.3s ease'
                            }} />
                          </Box>
                        </Box>
                      )}
                      <Box sx={{ bgcolor: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', overflow: 'hidden', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
                        <Box sx={{ px: 1.5, py: 1.2, borderBottom: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
                          <Typography sx={{ fontWeight: 600, fontSize: 12, color: '#0f172a', letterSpacing: '-0.01em' }}>
                            Salary Summary
                          </Typography>
                        </Box>
                        <Box sx={{ p: 1.5 }}>
                          <Box sx={{ mb: 1.5 }}>
                            <Typography sx={{ fontSize: 10, color: '#64748b', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.3 }}>Annual CTC</Typography>
                            <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>₹{fmt(ctc)}</Typography>
                          </Box>
                          <Box sx={{ mb: 1.5 }}>
                            <Typography sx={{ fontSize: 10, color: '#64748b', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.3 }}>Monthly Gross</Typography>
                            <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>₹{fmt(monthlyGross)}</Typography>
                          </Box>
                          <Box sx={{ height: '1px', bgcolor: '#e2e8f0', my: 1.2 }} />
                          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.8 }}>
                            <Typography sx={{ fontSize: 11, color: '#475569' }}>Total Allowances</Typography>
                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#0f172a' }}>₹{fmt(allocated)}</Typography>
                          </Stack>
                          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.2 }}>
                            <Typography sx={{ fontSize: 11, color: '#475569' }}>Total Deductions</Typography>
                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#dc2626' }}>−₹{fmt(totalDed)}</Typography>
                          </Stack>
                          <Box sx={{ p: 1.2, bgcolor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px' }}>
                            <Typography sx={{ fontSize: 10, color: '#15803d', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.3 }}>Net Take Home</Typography>
                            <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#15803d' }}>₹{fmt(netPay)}</Typography>
                          </Box>
                        </Box>
                      </Box>
                      </Box>
                    );
                  })()}
                </Grid>
              </>
            )}

            {tab === 'loan' && (
              <>
                <Grid item xs={12}>
                  <FieldLabel>Employee *</FieldLabel>
                  <Autocomplete
                    size="small"
                    options={employees}
                    value={loanEmployee}
                    onChange={(_, opt: any) => { setLoanEmployee(opt || null); setLoanForm(prev => ({ ...prev, employee: opt?._id || '' })); }}
                    onInputChange={(_, val, reason) => {
                      if (reason !== 'input') return;
                      const q = val.trim();
                      clearTimeout(empSearchTimer.current);
                      // search from 3 characters; cleared input goes back to the first 10
                      if (q.length > 0 && q.length < 3) return;
                      empSearchTimer.current = setTimeout(() => loadEmployees(q), 300);
                    }}
                    getOptionLabel={(opt: any) => `${opt?.name || 'Unnamed'} (${opt?.empId || '—'})`}
                    isOptionEqualToValue={(opt: any, val: any) => opt?._id === val?._id}
                    filterOptions={(opts) => opts}
                    renderInput={(params) => <TextField {...params} placeholder="Type name, ID..." size="small" sx={fieldSx} />}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FieldLabel>Loan Type</FieldLabel>
                  <FormControl fullWidth size="small" sx={selectSx}>
                    <Select value={loanForm.loanType} onChange={(e) => setLoanForm(prev => ({ ...prev, loanType: e.target.value }))}>
                      <MenuItem value="Salary Advance">Salary Advance</MenuItem>
                      <MenuItem value="Personal Loan">Personal Loan</MenuItem>
                      <MenuItem value="Education Loan">Education Loan</MenuItem>
                      <MenuItem value="Medical">Medical</MenuItem>
                      <MenuItem value="Other">Other</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FieldLabel>Status</FieldLabel>
                  <FormControl fullWidth size="small" sx={selectSx}>
                    <Select value={loanForm.status} onChange={(e) => setLoanForm(prev => ({ ...prev, status: e.target.value }))}>
                      <MenuItem value="Active">Active</MenuItem>
                      <MenuItem value="Paused">Paused</MenuItem>
                      <MenuItem value="Closed">Closed</MenuItem>
                      <MenuItem value="Cancelled">Cancelled</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FieldLabel>Principal Amount *</FieldLabel>
                  <TextField fullWidth size="small" sx={fieldSx} type="number" value={loanForm.principalAmount} onChange={(e) => setLoanForm(prev => ({ ...prev, principalAmount: e.target.value }))} InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>₹</Typography> }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FieldLabel>Monthly EMI *</FieldLabel>
                  <TextField fullWidth size="small" sx={fieldSx} type="number" value={loanForm.emiAmount} onChange={(e) => setLoanForm(prev => ({ ...prev, emiAmount: e.target.value }))} InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>₹</Typography> }} />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <FieldLabel>Total Months *</FieldLabel>
                  <TextField fullWidth size="small" sx={fieldSx} type="number" value={loanForm.totalMonths} onChange={(e) => setLoanForm(prev => ({ ...prev, totalMonths: e.target.value }))} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FieldLabel>Paid Months</FieldLabel>
                  <TextField fullWidth size="small" sx={fieldSx} type="number" value={loanForm.paidMonths} onChange={(e) => setLoanForm(prev => ({ ...prev, paidMonths: e.target.value }))} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FieldLabel>Interest Rate (%)</FieldLabel>
                  <TextField fullWidth size="small" sx={fieldSx} type="number" value={loanForm.interestRate} onChange={(e) => setLoanForm(prev => ({ ...prev, interestRate: e.target.value }))} InputProps={{ endAdornment: <Typography sx={{ ml: 1 }}>%</Typography> }} />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FieldLabel>Start Date *</FieldLabel>
                  <TextField fullWidth size="small" sx={fieldSx} type="date" InputLabelProps={{ shrink: true }} value={loanForm.startDate} onChange={(e) => setLoanForm(prev => ({ ...prev, startDate: e.target.value }))} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FieldLabel>End Date (auto if blank)</FieldLabel>
                  <TextField fullWidth size="small" sx={fieldSx} type="date" InputLabelProps={{ shrink: true }} value={loanForm.endDate} onChange={(e) => setLoanForm(prev => ({ ...prev, endDate: e.target.value }))} />
                </Grid>

                <Grid item xs={12}>
                  <FieldLabel>Purpose</FieldLabel>
                  <TextField fullWidth size="small" sx={fieldSx} placeholder="e.g. Home renovation" value={loanForm.purpose} onChange={(e) => setLoanForm(prev => ({ ...prev, purpose: e.target.value }))} />
                </Grid>
                <Grid item xs={12}>
                  <FieldLabel>Remarks</FieldLabel>
                  <TextField fullWidth size="small" sx={fieldSx} multiline minRows={2} value={loanForm.remarks} onChange={(e) => setLoanForm(prev => ({ ...prev, remarks: e.target.value }))} />
                </Grid>
              </>
            )}
          </Grid>
          </Box>

          {(tab === 'salaryHead' || tab === 'salaryGrade' || tab === 'loan') && (
            <DialogActions>
              <Button variant='outlined' sx={tab === 'loan' ? cancelButtonSx : undefined} onClick={() => setDrawerOpen(false)}>
                Cancel
              </Button>
              <Button variant="contained" onClick={handleDrawerSave} disabled={saving}>
                {saving ? 'Saving...' : tab === 'loan' ? (loanForm._id ? 'Update' : 'Create') : tab === 'salaryHead' ? (salaryHeadForm._id ? 'Update' : 'Create') : (salaryGradeForm._id ? 'Update' : 'Save')}
              </Button>
            </DialogActions>
          )}
        </Box>
      </Modal>

      <ConfirmDialog
        open={!!deleteTarget}
        title={`Delete ${deleteLabel.type}`}
        message={<>Are you sure you want to delete {deleteLabel.name ? <b>{deleteLabel.name}</b> : `this ${deleteLabel.type.toLowerCase()}`}?</>}
        loading={deleting}
        onConfirm={handleDelete}
        onClose={() => !deleting && setDeleteTarget(null)}
      />

      <Dialog
        open={headTypeDialogOpen}
        onClose={() => { setHeadTypeDialogOpen(false); setNewHeadTypeName(''); }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>{editingHeadTypeId ? 'Edit Head Type' : 'Add Head Type'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.5, color: '#374151' }}>Name *</Typography>
            <TextField
              fullWidth
              size="small"
              autoFocus
              placeholder="Enter head type name"
              value={newHeadTypeName}
              onChange={(e) => setNewHeadTypeName(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setHeadTypeDialogOpen(false); setNewHeadTypeName(''); setEditingHeadTypeId(''); }}>Cancel</Button>
          <Button
            variant="contained"
            disabled={savingHeadType || !newHeadTypeName.trim()}
            onClick={async () => {
              setSavingHeadType(true);
              try {
                let saved: any = null;
                if (editingHeadTypeId) {
                  const response = await axios.put(`/api/v1/payroll/salaryHeadType/${editingHeadTypeId}`, { name: newHeadTypeName.trim() });
                  saved = response.data?.data;
                } else {
                  const response = await axios.post('/api/v1/payroll/salaryHeadType', { name: newHeadTypeName.trim() });
                  saved = response.data?.data;
                }
                if (saved?._id) {
                  await loadHeadTypes();
                  if (tab === 'headType') {
                    await loadRows();
                  } else {
                    setSalaryHeadForm(prev => ({ ...prev, headType: saved._id } as any));
                  }
                  toast.success(editingHeadTypeId ? 'Head type updated' : 'Head type created');
                  setHeadTypeDialogOpen(false);
                  setNewHeadTypeName('');
                  setEditingHeadTypeId('');
                }
              } catch (err: any) {
                toast.error(err?.response?.data?.message || 'Failed to save head type');
              } finally {
                setSavingHeadType(false);
              }
            }}
          >
            {savingHeadType ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PayrollSettings;
